import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Plane, Clock, Filter, ChevronLeft, ArrowUpDown, X, Tag, Zap, Timer, Luggage, Utensils, ChevronUp, ChevronDown, ArrowRightLeft, Search, MapPin, Calendar, Users, Plus, Minus, CircleUser, UserRound, Baby, ChevronRight, AlertCircle } from 'lucide-react';
import { airports, airlines } from '../data/mock';
import { fetchFareRulesForFlight } from '../utils/fareRulesRouter';
import { isDomesticRoute } from '../utils/domesticAirports';
import { useAgentAuth } from '../context/AgentAuthContext';
import BottomSheet from '../components/BottomSheet';
import DesktopModifySearch from '../components/DesktopModifySearch';
import RoundTripFilterSidebar from '../components/RoundTripFilterSidebar';
import FareRulesCompact from '../components/FareRulesCompact';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import { deriveCodeshare } from '../utils/tripjackDerive';
import { rememberCurrentSearchUrl } from '../utils/lastSearchUrl';
import { tripBaggageBySegmentFromPriceInfo } from '../utils/tripjackTripBaggage';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import axios from 'axios';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sprint-5 · Domestic airport codes now sourced from shared util that
// mirrors backend `INDIAN_AIRPORTS` (50 codes). Local 33-code list was
// a silent regression vector for India routes through PAT/DED/IXM/etc.
// Use `isDomesticRoute(from, to)` from `utils/domesticAirports` instead.

// Helper function to get refundability badge info
const getRefundabilityInfo = (refundableType) => {
  switch (refundableType) {
    case 1:
      return {
        label: 'Refundable',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
      };
    case 2:
      return {
        label: 'Partially Refundable',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-200'
      };
    case 0:
    default:
      return {
        label: 'Non-Refundable',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200'
      };
  }
};


// Skeleton Loader Component
const FlightSkeleton = () => (
  <div className="bg-white rounded-lg p-4 mb-3 animate-pulse">
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-between items-center mb-3">
      <div className="h-10 bg-gray-200 rounded w-20"></div>
      <div className="flex-1 mx-4">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-20"></div>
    </div>
    <div className="flex justify-between items-center">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-10 bg-teal-100 rounded w-32"></div>
    </div>
  </div>
);

const RoundTripResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { agent: agentSession } = useAgentAuth();
  
  // Modify Search State
  const [showModifySearch, setShowModifySearch] = useState(false);
  const [showMobileModifySearch, setShowMobileModifySearch] = useState(false);
  const [showOnwardFilters, setShowOnwardFilters] = useState(false);
  const [showReturnFilters, setShowReturnFilters] = useState(false);
  const [tripType, setTripType] = useState('roundTrip');
  const [fromAirport, setFromAirport] = useState(searchParams.get('from') || 'BOM');
  const [toAirport, setToAirport] = useState(searchParams.get('to') || 'DXB');
  const [departureDate, setDepartureDate] = useState(searchParams.get('date') || '2024-12-05');
  const [returnDate, setReturnDate] = useState(searchParams.get('return') || '2024-12-15');
  const [adults, setAdults] = useState(Number(searchParams.get('adults')) || 1);
  const [children, setChildren] = useState(Number(searchParams.get('children')) || 0);
  const [infants, setInfants] = useState(Number(searchParams.get('infants')) || 0);
  const [travelClass, setTravelClass] = useState(searchParams.get('class') || 'Economy');

  // Sync state with URL params whenever they change
  useEffect(() => {
    const newFrom = searchParams.get('from') || 'BOM';
    const newTo = searchParams.get('to') || 'DXB';
    const newDate = searchParams.get('date') || '2024-12-05';
    const newReturn = searchParams.get('return') || '2024-12-15';
    const newAdults = Number(searchParams.get('adults')) || 1;
    const newChildren = Number(searchParams.get('children')) || 0;
    const newInfants = Number(searchParams.get('infants')) || 0;
    const newClass = searchParams.get('class') || 'Economy';
    
    // Only update if values actually changed
    if (newFrom !== fromAirport) setFromAirport(newFrom);
    if (newTo !== toAirport) setToAirport(newTo);
    if (newDate !== departureDate) setDepartureDate(newDate);
    if (newReturn !== returnDate) setReturnDate(newReturn);
    if (newAdults !== adults) setAdults(newAdults);
    if (newChildren !== children) setChildren(newChildren);
    if (newInfants !== infants) setInfants(newInfants);
    if (newClass !== travelClass) setTravelClass(newClass);
  }, [searchParams]);

  useEffect(() => {
    rememberCurrentSearchUrl();
  }, [location.pathname, location.search]);

  const fromCode = fromAirport;
  const toCode = toAirport;

  // Selection state
  const [selectedOnward, setSelectedOnward] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  // Special Return UX toggle: when ON, show SPECIAL_RETURN fares only and pair return by msri⊃sri.
  const [specialReturnMode, setSpecialReturnMode] = useState(false);

  // API Data state
  const [onwardFlightsData, setOnwardFlightsData] = useState([]);
  const [returnFlightsData, setReturnFlightsData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state for performance
  const [onwardDisplayCount, setOnwardDisplayCount] = useState(20);
  const [returnDisplayCount, setReturnDisplayCount] = useState(20);
  const ITEMS_PER_PAGE = 20;
  
  const [activeTab, setActiveTab] = useState({});
  const [fareRules, setFareRules] = useState({});
  const [loadingFareRules, setLoadingFareRules] = useState({});

  // Filter states - shared across both onward and return
  const [selectedStops, setSelectedStops] = useState([]);
  const [selectedAirlines, setSelectedAirlines] = useState([]);
  const [selectedOnwardDepartureTimes, setSelectedOnwardDepartureTimes] = useState([]);
  const [selectedOnwardArrivalTimes, setSelectedOnwardArrivalTimes] = useState([]);
  const [selectedReturnDepartureTimes, setSelectedReturnDepartureTimes] = useState([]);
  const [selectedReturnArrivalTimes, setSelectedReturnArrivalTimes] = useState([]);
  const [priceRange, setPriceRange] = useState([5000, 70000]);
  const [expandedFlight, setExpandedFlight] = useState(null); // Track expanded flight ID
  
  // INDEPENDENT sorting and date selection for Onward and Return
  const [onwardSortBy, setOnwardSortBy] = useState('price');
  const [returnSortBy, setReturnSortBy] = useState('price');
  const [selectedOnwardDateIdx, setSelectedOnwardDateIdx] = useState(0);
  const [selectedReturnDateIdx, setSelectedReturnDateIdx] = useState(0);
  
  // Mobile tab state
  const [mobileActiveTab, setMobileActiveTab] = useState('onward'); // 'onward' or 'return'
  
  const [airlineLogos, setAirlineLogos] = useState({});

  // Check if route is domestic only — uses shared util mirrored to backend.
  const isDomestic = isDomesticRoute(fromCode, toCode);

  const formatINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

  // ───────────────────────────────────────────────────────────────
  // Tripjack multi-fare support (industry standard)
  // Each Tripjack `trip` can have many fares in totalPriceList[].
  // We keep one flight card (segments) and allow selecting a fare option.
  // ───────────────────────────────────────────────────────────────
  const normaliseTripjackFareOption = (fare = {}) => {
    const adultFC = fare?.fd?.ADULT?.fC || {};
    const childFC = fare?.fd?.CHILD?.fC || {};
    const infantFC = fare?.fd?.INFANT?.fC || {};
    const fi = fare?.fareIdentifier || fare?.fare_identifier || '';
    const sri = fare?.sri || null;
    const msriRaw = fare?.msri ?? fare?.mSri ?? null;
    const msri =
      Array.isArray(msriRaw) ? msriRaw
      : typeof msriRaw === 'string' ? msriRaw.split(',').map(s => s.trim()).filter(Boolean)
      : [];
    const ad = fare?.fd?.ADULT || {};
    return {
      id: fare?.id,
      fareIdentifier: fi,
      fareType: fi || 'Standard',
      sri,
      msri,
      seatAvailable: fare?.seatAvailable,
      refundableType: fare?.fd?.ADULT?.rT,
      fareRuleInformation: fare?.fareRuleInformation || null,
      fares: {
        adult: Math.round(adultFC?.TF || 0),
        child: Math.round(childFC?.TF || adultFC?.TF || 0),
        infant: Math.round(infantFC?.TF || 0),
      },
      price: adultFC?.TF || 0,
      baseFare: adultFC?.BF || 0,
      taxesAndFees: (adultFC?.TAF || 0) + (adultFC?.OT || 0),
      baggage: ad.bI?.iB || null,
      cabinBaggage: ad.bI?.cB || null,
      tripBaggageBySegmentId: tripBaggageBySegmentFromPriceInfo(fare) || undefined,
    };
  };

  const applyFareToFlight = (flight, fareOpt) => {
    if (!flight || !fareOpt) return flight;
    return {
      ...flight,
      selectedFareId: fareOpt.id,
      priceId: fareOpt.id,
      price: fareOpt.price,
      baseFare: fareOpt.baseFare,
      taxesAndFees: fareOpt.taxesAndFees,
      fares: fareOpt.fares,
      refundableType: fareOpt.refundableType,
      seatsLeft: fareOpt.seatAvailable,
      fareRuleInfo: fareOpt.fareRuleInformation,
      fareIdentifier: fareOpt.fareIdentifier,
      // Bug-fix Feb-2026 · `fareType` (display chip) and `originalPrice`
      // (used by /booking review banner) were leaking from the default fare
      // when the user picked a different chip. See SearchResults.jsx Book
      // button for the full RCA.
      fareType: fareOpt.fareType || flight.fareType,
      originalPrice: fareOpt.price,
      sri: fareOpt.sri,
      msri: fareOpt.msri,
      baggage: fareOpt.baggage ?? flight.baggage,
      cabinBaggage: fareOpt.cabinBaggage ?? flight.cabinBaggage,
      tripBaggageBySegmentId: fareOpt.tripBaggageBySegmentId ?? flight.tripBaggageBySegmentId,
    };
  };

  const cheapestFareOption = (fareOptions = []) => {
    if (!Array.isArray(fareOptions) || fareOptions.length === 0) return null;
    return [...fareOptions].sort((a, b) => (Number(a.price || 0) - Number(b.price || 0)))[0] || null;
  };

  const cheapestByIdentifier = (fareOptions = [], identifier) => {
    const iden = String(identifier || '').toUpperCase();
    const filtered = (fareOptions || []).filter(o => String(o?.fareIdentifier || '').toUpperCase() === iden);
    return cheapestFareOption(filtered);
  };

  const cheapestPairedSpecialReturn = (fareOptions = [], requiredSri) => {
    const req = requiredSri ? String(requiredSri).trim() : '';
    if (!req) return null;
    const filtered = (fareOptions || []).filter(o =>
      String(o?.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN'
      && Array.isArray(o?.msri)
      && o.msri.map(x => String(x).trim()).includes(req)
    );
    return cheapestFareOption(filtered);
  };

  // Transform Tripjack flight data
  const transformTripjackFlights = (tripjackFlights) => {
    return tripjackFlights.map((trip, index) => {
      const firstSegment = trip.sI?.[0] || {};
      const lastSegment = trip.sI?.[trip.sI.length - 1] || {};
      const firstFd = firstSegment.fD || {};
      const codeshare = deriveCodeshare(firstFd);

      const rawFareOptions = trip.totalPriceList || [];
      const fareOptions = rawFareOptions.map(normaliseTripjackFareOption).filter(o => o && o.id);
      const defaultFare = cheapestFareOption(fareOptions) || normaliseTripjackFareOption(rawFareOptions[0] || {});
      const adultFare = (rawFareOptions[0] || {}).fd?.ADULT?.fC || {};
      const childFare = (rawFareOptions[0] || {}).fd?.CHILD?.fC || {};
      const infantFare = (rawFareOptions[0] || {}).fd?.INFANT?.fC || {};
      // Industry-standard "lead price" = cheapest offer across all fare options.
      // List sort + price-range filter MUST evaluate against this envelope, not
      // the user's currently-selected chip — otherwise the card moves/disappears
      // every time a chip is clicked.
      const farePrices = fareOptions.map(o => Number(o.price || 0)).filter(n => n > 0);
      const leadPrice = farePrices.length ? Math.min(...farePrices) : (defaultFare?.price || 0);
      const fareEnvelope = {
        minPrice: leadPrice,
        maxPrice: farePrices.length ? Math.max(...farePrices) : leadPrice,
        anyRefundable: fareOptions.some(o => o.refundableType === 1 || o.refundableType === 2),
      };
      
      // Calculate total duration from segments
      const totalDuration = trip.sI?.reduce((sum, seg) => sum + (seg.duration || 0), 0) || 0;
      
      // Stable trip identity (do NOT tie to fare id)
      const tripKey = [
        'tj',
        firstSegment?.da?.code || fromCode,
        lastSegment?.aa?.code || toCode,
        firstSegment?.dt || '',
        lastSegment?.at || '',
        firstFd?.aI?.code || '',
        firstFd?.fN || '',
        String(index),
      ].join('_');

      return {
        id: tripKey,
        priceId: defaultFare?.id,
        // ⚠️ source MUST be stamped so agent-booking routes to /api/tripjack/book.
        source: 'tripjack',
        airline: firstSegment.fD?.aI?.name || 'Airline',
        airlineCode: firstSegment.fD?.aI?.code || 'XX',
        flightNumber: firstSegment.fD?.fN || 'N/A',
        departureTime: firstSegment.dt?.split('T')[1]?.substring(0, 5) || '00:00',
        arrivalTime: lastSegment.at?.split('T')[1]?.substring(0, 5) || '00:00',
        departureDateTime: firstSegment.dt || '',
        arrivalDateTime: lastSegment.at || '',
        from: firstSegment.da?.code || fromCode,
        to: lastSegment.aa?.code || toCode,
        fromCity: firstSegment.da?.city || '',
        toCity: lastSegment.aa?.city || '',
        fromAirport: firstSegment.da?.name || firstSegment.da?.terminal || '',
        toAirport: lastSegment.aa?.name || lastSegment.aa?.terminal || '',
        duration: `${Math.floor(totalDuration / 60)}h ${totalDuration % 60}m`,
        stops: trip.sI?.length > 1 ? `${trip.sI.length - 1} Stop${trip.sI.length > 2 ? 's' : ''}` : 'Non Stop',
        price: defaultFare?.price || (adultFare.TF || 0),
        baseFare: defaultFare?.baseFare || (adultFare.BF || 0),
        taxesAndFees: defaultFare?.taxesAndFees || ((adultFare.TAF || 0) + (adultFare.OT || 0)),
        // Per-pax-type TFs (PDF lines 595-621) — used by BookingPage cart sum.
        fares: {
          adult: Math.round(defaultFare?.fares?.adult || adultFare.TF || 0),
          child: Math.round(defaultFare?.fares?.child || childFare.TF || adultFare.TF || 0),
          infant: Math.round(defaultFare?.fares?.infant || infantFare.TF || 0),
        },
        class: 'Economy',
        baggage: defaultFare?.baggage || trip.sI?.[0]?.ssrInfo?.BAGGAGE?.[0]?.desc || firstSegment.fD?.bI?.iB || null,
        cabinBaggage: defaultFare?.cabinBaggage || firstSegment.fD?.bI?.cB || null,
        refundableType: defaultFare?.refundableType, // 0=Non-refundable, 1=Refundable, 2=Partially refundable
        seatsLeft: defaultFare?.seatAvailable,
        mealIncluded: trip.sI?.some(seg => seg.ssrInfo?.MEAL?.length > 0) || false,
        // Wave-6 R4 · Codeshare (operated-by) info for FlightCard chip
        operatingAirline: codeshare.operatingAirline,
        operatingAirlineCode: codeshare.operatingAirlineCode,
        isCodeshare: codeshare.isCodeshare,
        segments: trip.sI || [],
        fareRuleInfo: defaultFare?.fareRuleInformation || null,
        fareOptions,
        selectedFareId: defaultFare?.id || null,
        fareIdentifier: defaultFare?.fareIdentifier || '',
        sri: defaultFare?.sri || null,
        msri: defaultFare?.msri || [],
        tripBaggageBySegmentId: defaultFare?.tripBaggageBySegmentId,
        leadPrice,
        fareEnvelope,
      };
    });
  };

  // Adapter: convert a Riya (normalised) flight into the same card shape this page uses
  // so filters/sorting/display work uniformly with Tripjack-transformed cards.
  const adaptRiyaToCardShape = (riyaFlights) => {
    return (riyaFlights || []).map((r, idx) => {
      // Extract HH:MM from ISO
      const depHHMM = (r.departureTime || '').split('T')[1]?.substring(0, 5) || '00:00';
      const arrHHMM = (r.arrivalTime || '').split('T')[1]?.substring(0, 5) || '00:00';
      return {
        id: r.id || r.priceId || `riya-${idx}`,
        priceId: r.priceId || r.id,
        airline: r.airline || 'Airline',
        airlineCode: r.airlineCode || 'XX',
        flightNumber: r.flightNumber || 'N/A',
        departureTime: depHHMM,
        arrivalTime: arrHHMM,
        departureDateTime: r.departureTime || '',
        arrivalDateTime: r.arrivalTime || '',
        from: r.from || fromCode,
        to: r.to || toCode,
        fromCity: r.fromCity || '',
        toCity: r.toCity || '',
        fromAirport: r.fromAirportName || '',
        toAirport: r.toAirportName || '',
        duration: r.duration || '',
        stops: r.stops || 'Non Stop',
        price: r.price || 0,
        baseFare: r.baseFare || 0,
        taxesAndFees: r.tax || 0,
        class: r.classOfBooking || 'Economy',
        baggage: r.baggage || '15 Kg',
        cabinBaggage: r.cabinBaggage || '7 Kg',
        refundableType: r.refundable ? 1 : 0,
        seatsLeft: r.seatsLeft || 9,
        mealIncluded: !!r.mealIncluded,
        segments: r.segments || [],
        fareRuleInfo: null,
        source: 'riya',
        // Carry-through ALL Riya fields needed for the booking flow
        // (Pricing → Book → IssueTicket). Missing any of these causes the
        // backend to reject /Pricing with "(&8) TrackId tag is empty".
        riyaTrackId: r.riyaTrackId,
        riyaFlightId: r.riyaFlightId,
        riyaFlightIds: r.riyaFlightIds,
        riyaBaseAmount: r.riyaBaseAmount,
        riyaGrossAmount: r.riyaGrossAmount,
        riyaSearchedAt: r.riyaSearchedAt,
        riyaItinRef: r.riyaItinRef,
        riyaSegRef: r.riyaSegRef,
        riyaReferenceToken: r.riyaReferenceToken,
        riyaAllowBlockPNR: r.riyaAllowBlockPNR,
        riyaAirlineCategory: r.riyaAirlineCategory,
        riyaSegmentType: r.riyaSegmentType,
        segmentType: r.segmentType,
        riyaCombo: r.riyaCombo,
        riyaComboGroupId: r.riyaComboGroupId,
        fares: r.fares,
        raw: r,
        // Riya does not currently surface branded-fare envelopes; treat as
        // single-offer so sort/filter still works uniformly.
        fareOptions: [],
        leadPrice: r.price || 0,
        fareEnvelope: { minPrice: r.price || 0, maxPrice: r.price || 0, anyRefundable: !!r.refundable },
      };
    });
  };

  // Fetch round trip flights from the unified /search-all endpoint (Tripjack + Riya + Offline)
  useEffect(() => {
    const fetchFlights = async () => {
      if (!isDomestic) {
        console.log('International route detected - skipping API call');
        return;
      }

      setLoading(true);
      try {
        // Sprint-6 · Forward industry-form params from URL (B3/B4/B5)
        const _pft = searchParams.get('pft') || undefined;
        const _preferred = searchParams.get('preferred_airlines') || undefined;
        const _directOnly = searchParams.get('direct_only') === '1';
        const _connectingOnly = searchParams.get('connecting_only') === '1';

        const response = await axios.get(`${API}/flights/search-all`, {
          params: {
            from_airport: fromCode,
            to_airport: toCode,
            date: departureDate,
            return_date: returnDate,
            adults: adults,
            children: children,
            infants: infants,
            class_type: travelClass,
            include_tripjack: true,
            include_offline: true,
            include_riya: true,
            ...(_pft ? { pft: _pft } : {}),
            ...(_preferred ? { preferred_airlines: _preferred } : {}),
            ...(_directOnly ? { direct_only: true } : {}),
            ...(_connectingOnly ? { connecting_only: true } : {}),
            // Mini-Sprint 1 · markup-engine context (per-leg agent rule resolution)
            user_type: agentSession?.id ? 'agent' : 'b2c',
            ...(agentSession?.id ? { agent_id: agentSession.id } : {}),
          },
        });

        console.log('Unified RT search response:', response.data);

        const tripjackOnward = response.data?.tripjack_onward_raw || [];
        const tripjackReturn = response.data?.tripjack_return_raw || [];
        const riyaOnward = response.data?.riya_onward_flights || [];
        const riyaReturn = response.data?.riya_return_flights || [];
        // Offline flights already ship in FlightCard-compatible shape
        // (format_offline_flight_for_display on the backend) — no transform
        // needed, unlike Tripjack/Riya which require adapters.
        const offlineOnward = response.data?.offline_flights || [];
        const offlineReturn = response.data?.offline_return_flights || [];

        const onwardMerged = [
          ...offlineOnward,
          ...transformTripjackFlights(tripjackOnward),
          ...adaptRiyaToCardShape(riyaOnward),
        ];
        const returnMerged = [
          ...offlineReturn,
          ...transformTripjackFlights(tripjackReturn),
          ...adaptRiyaToCardShape(riyaReturn),
        ];

        setOnwardFlightsData(onwardMerged);
        setReturnFlightsData(returnMerged);

        console.log(`Loaded ${onwardMerged.length} onward (${tripjackOnward.length} Tripjack + ${riyaOnward.length} Riya + ${offlineOnward.length} Offline) and ${returnMerged.length} return flights`);
      } catch (error) {
        console.error('Error fetching round trip flights:', error);
        setOnwardFlightsData([]);
        setReturnFlightsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFlights();
  }, [fromCode, toCode, departureDate, returnDate, adults, children, infants, travelClass, isDomestic, searchParams]);

  // Fetch fare rules — cached per (flightId, priceId) so different fares on the
  // same flight resolve to their own rule set.
  const fareRuleKey = (flightId, priceId) => `${flightId}__${priceId || ''}`;
  // Sprint-5 · Supplier-aware fare-rules fetch (S1/R1).
  const fetchFareRules = async (priceId, flightId, flight = null) => {
    const key = fareRuleKey(flightId, priceId);
    if (fareRules[key]) return;

    setLoadingFareRules(prev => ({ ...prev, [key]: true }));

    try {
      const fl = flight || [...(onwardFlightsData || []), ...(returnFlightsData || [])].find(f => `flight-${f.id}` === flightId || f.id === flightId) || null;
      const response = await fetchFareRulesForFlight(fl || { source: 'tripjack' }, priceId);
      if (response.data) {
        setFareRules(prev => ({ ...prev, [key]: response.data }));
      }
    } catch (error) {
      console.error('Error fetching fare rules:', error);
      setFareRules(prev => ({
        ...prev,
        [key]: { error: 'Fare rules are temporarily unavailable. Please check the airline website for cancellation and change policies.' }
      }));
    } finally {
      setLoadingFareRules(prev => ({ ...prev, [key]: false }));
    }
  };

  // Sprint-5 · Logo prefetch derived from RESPONSE flights, not mock airline list (R3).
  useEffect(() => {
    const allFlights = [...(onwardFlightsData || []), ...(returnFlightsData || [])];
    if (allFlights.length === 0) return;
    const codeSet = new Set();
    allFlights.forEach(f => {
      if (f?.airlineCode) codeSet.add(f.airlineCode);
      (f?.segments || []).forEach(s => {
        const c = s?.fD?.aI?.code || s?.airlineCode;
        if (c) codeSet.add(c);
      });
    });
    if (codeSet.size === 0) return;
    fetchAirlineLogos(Array.from(codeSet))
      .then(data => setAirlineLogos(prev => ({ ...prev, ...data })))
      .catch(err => console.error('Airline logo fetch error:', err));
  }, [onwardFlightsData, returnFlightsData]);

  const fromAirportInfo = airports.find((a) => a.code === fromCode);
  const toAirportInfo = airports.find((a) => a.code === toCode);

  // Calculate unique airlines from both onward and return flights
  const uniqueAirlines = useMemo(() => {
    const airlineMap = new Map();
    const allFlights = [...onwardFlightsData, ...returnFlightsData];
    allFlights.forEach(flight => {
      if (!airlineMap.has(flight.airlineCode)) {
        airlineMap.set(flight.airlineCode, {
          code: flight.airlineCode,
          name: flight.airline,
          count: 1
        });
      } else {
        airlineMap.get(flight.airlineCode).count++;
      }
    });
    return Array.from(airlineMap.values());
  }, [onwardFlightsData, returnFlightsData]);

  // Calculate stop counts
  const stopCounts = useMemo(() => {
    const allFlights = [...onwardFlightsData, ...returnFlightsData];
    return {
      nonStop: allFlights.filter(f => f.stops === 'Non Stop').length,
      oneStop: allFlights.filter(f => f.stops.includes('1 Stop')).length,
      twoPlus: allFlights.filter(f => f.stops.includes('2 Stop') || f.stops.includes('3 Stop')).length
    };
  }, [onwardFlightsData, returnFlightsData]);

  // Helper function to check if a time falls in a time slot
  const isTimeInSlot = (time, slotId) => {
    const hour = parseInt(time.split(':')[0]);
    if (slotId === 'earlyMorning') return hour >= 0 && hour < 6;
    if (slotId === 'morning') return hour >= 6 && hour < 12;
    if (slotId === 'afternoon') return hour >= 12 && hour < 18;
    if (slotId === 'evening') return hour >= 18 && hour < 24;
    return false;
  };

  // Filter and sort onward flights
  //
  // Industry-standard behavior (Google Flights / Skyscanner / MMT / Cleartrip /
  // IATA NDC OfferItem): the results list is sorted & filtered by the **lead
  // price** (cheapest offer per itinerary), not the user's currently-selected
  // fare chip. Otherwise clicking a chip would re-sort the list or drop the
  // card out of the price-range filter — both of which feel like "the card
  // changed". We use `leadPrice` and `fareEnvelope` stamped by the transform.
  const onwardFlights = useMemo(() => {
    let flights = [...onwardFlightsData];

    if (specialReturnMode) {
      // Mode toggle is a *filter*, not a forced fare swap. We only keep
      // itineraries that have a SPECIAL_RETURN offer; the actual fare-chip
      // application is done once via a useEffect (see below) so the user's
      // chip selection is never overridden on every render.
      flights = flights.filter(f =>
        Array.isArray(f.fareOptions)
        && f.fareOptions.some(o => String(o.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN')
      );
    }
    
    // Stop filter
    if (selectedStops.length > 0) {
      flights = flights.filter((f) => {
        const stopCount = f.stops === 'Non Stop' ? 0 : parseInt(f.stops);
        if (selectedStops.includes(0) && f.stops === 'Non Stop') return true;
        if (selectedStops.includes(1) && f.stops.includes('1 Stop')) return true;
        if (selectedStops.includes(2) && (f.stops.includes('2 Stop') || f.stops.includes('3 Stop'))) return true;
        return false;
      });
    }
    
    // Airline filter
    if (selectedAirlines.length > 0) {
      flights = flights.filter((f) => selectedAirlines.includes(f.airlineCode));
    }

    // Onward departure time filter
    if (selectedOnwardDepartureTimes.length > 0) {
      flights = flights.filter((f) => 
        selectedOnwardDepartureTimes.some(slot => isTimeInSlot(f.departureTime, slot))
      );
    }

    // Onward arrival time filter
    if (selectedOnwardArrivalTimes.length > 0) {
      flights = flights.filter((f) => 
        selectedOnwardArrivalTimes.some(slot => isTimeInSlot(f.arrivalTime, slot))
      );
    }
    
    // Price filter — evaluated against the lead (cheapest available) price so
    // the card stays visible no matter which fare chip the user explores.
    flights = flights.filter((f) => {
      const p = Number(f.leadPrice ?? f.price ?? 0);
      return p >= priceRange[0] && p <= priceRange[1];
    });
    
    // Sort — `leadPrice` is the stable per-itinerary key (independent of chip).
    const priceKey = (f) => Number(f.leadPrice ?? f.price ?? 0);
    if (onwardSortBy === 'price') flights.sort((a, b) => priceKey(a) - priceKey(b));
    if (onwardSortBy === 'duration') flights.sort((a, b) => {
      const aDur = parseInt(a.duration);
      const bDur = parseInt(b.duration);
      return aDur - bDur;
    });
    if (onwardSortBy === 'departure') flights.sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    
    return flights;
  }, [onwardFlightsData, selectedStops, selectedAirlines, selectedOnwardDepartureTimes, selectedOnwardArrivalTimes, priceRange, onwardSortBy, specialReturnMode]);

  // Filter and sort return flights (independent sorting).
  //
  // Same industry-standard rule as onward: list mutations driven by `leadPrice`,
  // not the per-card selected chip. In Special Return mode we additionally
  // restrict to itineraries whose `msri` includes the selected onward's `sri`
  // (Tripjack v2.0 §"Special Return" pairing). The fare-chip application is
  // performed once in a useEffect, never inside this memo.
  const returnFlights = useMemo(() => {
    let flights = [...returnFlightsData];

    if (specialReturnMode) {
      const requiredSri = selectedOnward?.sri ? String(selectedOnward.sri).trim() : '';
      flights = flights.filter(f =>
        Array.isArray(f.fareOptions)
        && f.fareOptions.some(o => String(o.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN')
      );
      if (requiredSri) {
        // msri envelope: a return itinerary qualifies if ANY of its
        // SPECIAL_RETURN options is paired with the chosen onward's `sri`.
        flights = flights.filter(f =>
          Array.isArray(f.fareOptions)
          && f.fareOptions.some(o =>
            String(o.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN'
            && Array.isArray(o.msri)
            && o.msri.map(x => String(x).trim()).includes(requiredSri)
          )
        );
      }
    }
    
    // Stop filter
    if (selectedStops.length > 0) {
      flights = flights.filter((f) => {
        const stopCount = f.stops === 'Non Stop' ? 0 : parseInt(f.stops);
        if (selectedStops.includes(0) && f.stops === 'Non Stop') return true;
        if (selectedStops.includes(1) && f.stops.includes('1 Stop')) return true;
        if (selectedStops.includes(2) && (f.stops.includes('2 Stop') || f.stops.includes('3 Stop'))) return true;
        return false;
      });
    }
    
    // Airline filter
    if (selectedAirlines.length > 0) {
      flights = flights.filter((f) => selectedAirlines.includes(f.airlineCode));
    }

    // Return departure time filter
    if (selectedReturnDepartureTimes.length > 0) {
      flights = flights.filter((f) => 
        selectedReturnDepartureTimes.some(slot => isTimeInSlot(f.departureTime, slot))
      );
    }

    // Return arrival time filter
    if (selectedReturnArrivalTimes.length > 0) {
      flights = flights.filter((f) => 
        selectedReturnArrivalTimes.some(slot => isTimeInSlot(f.arrivalTime, slot))
      );
    }
    
    // Price filter — lead-price envelope (see onwardFlights comment).
    flights = flights.filter((f) => {
      const p = Number(f.leadPrice ?? f.price ?? 0);
      return p >= priceRange[0] && p <= priceRange[1];
    });
    
    // Sort by stable lead-price.
    const priceKey = (f) => Number(f.leadPrice ?? f.price ?? 0);
    if (returnSortBy === 'price') flights.sort((a, b) => priceKey(a) - priceKey(b));
    if (returnSortBy === 'duration') flights.sort((a, b) => {
      const aDur = parseInt(a.duration);
      const bDur = parseInt(b.duration);
      return aDur - bDur;
    });
    if (returnSortBy === 'departure') flights.sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    
    return flights;
  }, [returnFlightsData, selectedStops, selectedAirlines, selectedReturnDepartureTimes, selectedReturnArrivalTimes, priceRange, returnSortBy, specialReturnMode, selectedOnward?.sri]);

  useEffect(() => {
    // Avoid stale selection when toggling special-return mode.
    setSelectedOnward(null);
    setSelectedReturn(null);
    setOnwardDisplayCount(ITEMS_PER_PAGE);
    setReturnDisplayCount(ITEMS_PER_PAGE);
  }, [specialReturnMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot fare application for Special Return mode.
  //
  // Industry-standard pattern: when the "Special Return" filter is toggled ON
  // (or whenever the onward `sri` changes inside the mode), we initialise each
  // card's displayed fare to the cheapest paired SPECIAL_RETURN offer. Doing
  // this once via useEffect — instead of re-applying inside useMemo on every
  // render — preserves any subsequent chip selection by the user (you'll still
  // be restricted to SPECIAL chips while in this mode via the chip UI).
  useEffect(() => {
    if (!specialReturnMode) return;
    setOnwardFlightsData(prev => prev.map(f => {
      if (!Array.isArray(f.fareOptions) || f.fareOptions.length === 0) return f;
      const opt = cheapestByIdentifier(f.fareOptions, 'SPECIAL_RETURN');
      return opt ? applyFareToFlight(f, opt) : f;
    }));
  }, [specialReturnMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!specialReturnMode) return;
    const requiredSri = selectedOnward?.sri ? String(selectedOnward.sri).trim() : '';
    setReturnFlightsData(prev => prev.map(f => {
      if (!Array.isArray(f.fareOptions) || f.fareOptions.length === 0) return f;
      const opt = requiredSri
        ? (cheapestPairedSpecialReturn(f.fareOptions, requiredSri) || cheapestByIdentifier(f.fareOptions, 'SPECIAL_RETURN'))
        : cheapestByIdentifier(f.fareOptions, 'SPECIAL_RETURN');
      return opt ? applyFareToFlight(f, opt) : f;
    }));
  }, [specialReturnMode, selectedOnward?.sri]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paginated flights for performance (show 20 initially)
  const paginatedOnwardFlights = useMemo(() => {
    return onwardFlights.slice(0, onwardDisplayCount);
  }, [onwardFlights, onwardDisplayCount]);

  const paginatedReturnFlights = useMemo(() => {
    return returnFlights.slice(0, returnDisplayCount);
  }, [returnFlights, returnDisplayCount]);

  const handleLoadMoreOnward = () => {
    setOnwardDisplayCount(prev => prev + ITEMS_PER_PAGE);
  };

  const handleLoadMoreReturn = () => {
    setReturnDisplayCount(prev => prev + ITEMS_PER_PAGE);
  };

  // Infinite scroll sentinels — one per column per viewport (mobile + desktop)
  const hasMoreOnward = onwardDisplayCount < onwardFlights.length;
  const hasMoreReturn = returnDisplayCount < returnFlights.length;
  const onwardScrollRefMobile = useInfiniteScroll({
    hasMore: hasMoreOnward && !loading,
    onLoadMore: handleLoadMoreOnward,
  });
  const onwardScrollRefDesktop = useInfiniteScroll({
    hasMore: hasMoreOnward && !loading,
    onLoadMore: handleLoadMoreOnward,
  });
  const returnScrollRefMobile = useInfiniteScroll({
    hasMore: hasMoreReturn && !loading,
    onLoadMore: handleLoadMoreReturn,
  });
  const returnScrollRefDesktop = useInfiniteScroll({
    hasMore: hasMoreReturn && !loading,
    onLoadMore: handleLoadMoreReturn,
  });


  const toggleStop = (stop) => setSelectedStops((p) => p.includes(stop) ? p.filter((s) => s !== stop) : [...p, stop]);
  const toggleAirline = (code) => setSelectedAirlines((p) => p.includes(code) ? p.filter((c) => c !== code) : [...p, code]);

  const cheapestOnwardPrice = onwardFlights.length > 0 ? Math.min(...onwardFlights.map(f => f.price)) : 0;
  const cheapestReturnPrice = returnFlights.length > 0 ? Math.min(...returnFlights.map(f => f.price)) : 0;

  const totalPrice = (selectedOnward?.price || 0) + (selectedReturn?.price || 0);
  const bothSelected = selectedOnward && selectedReturn;

  // Build the per-flight chip list with the cheapest price for each fare
  // identifier plus a ₹-delta against the lead price and an `isLead` flag for
  // the "Cheapest" badge. This is the data backing the industry-standard
  // branded-fare chip UI (delta + Cheapest badge on the lead chip).
  const fareIdentifierChips = (flight) => {
    const opts = Array.isArray(flight?.fareOptions) ? flight.fareOptions : [];
    const byIden = new Map();
    for (const o of opts) {
      const iden = String(o?.fareIdentifier || '').toUpperCase();
      if (!iden) continue;
      const price = Number(o?.price || 0);
      const prev = byIden.get(iden);
      if (!prev || price < prev) byIden.set(iden, price);
    }
    if (byIden.size === 0) return [];
    const order = ['PUBLISHED', 'SPECIAL_RETURN', 'CORPORATE', 'TJ_FLEX'];
    const minPrice = Math.min(...Array.from(byIden.values()));
    return Array.from(byIden.entries())
      .map(([iden, price]) => ({
        iden,
        price,
        delta: Math.max(0, Math.round(price - minPrice)),
        isLead: price === minPrice,
      }))
      .sort((a, b) => {
        const ai = order.indexOf(a.iden);
        const bi = order.indexOf(b.iden);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      });
  };

  const setFareForOnwardFlight = (flight, identifier) => {
    const opt = cheapestByIdentifier(flight?.fareOptions || [], identifier);
    if (!opt) return;
    setOnwardFlightsData(prev => prev.map(f => (f.id === flight.id ? applyFareToFlight(f, opt) : f)));
    if (selectedOnward?.id === flight.id) setSelectedOnward(applyFareToFlight(selectedOnward, opt));
  };

  const setFareForReturnFlight = (flight, identifier) => {
    const opt = cheapestByIdentifier(flight?.fareOptions || [], identifier);
    if (!opt) return;
    setReturnFlightsData(prev => prev.map(f => (f.id === flight.id ? applyFareToFlight(f, opt) : f)));
    if (selectedReturn?.id === flight.id) setSelectedReturn(applyFareToFlight(selectedReturn, opt));
  };

  const handleContinue = () => {
    if (bothSelected) {
      if (specialReturnMode) {
        const sri = selectedOnward?.sri ? String(selectedOnward.sri).trim() : '';
        const ok =
          sri
          && String(selectedOnward?.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN'
          && String(selectedReturn?.fareIdentifier || '').toUpperCase() === 'SPECIAL_RETURN'
          && Array.isArray(selectedReturn?.msri)
          && selectedReturn.msri.map(x => String(x).trim()).includes(sri);
        if (!ok) {
          alert('Special Return requires a compatible return fare. Please reselect your return flight.');
          return;
        }
      }
      // Store both onward and return flight data
      const bookingData = {
        type: 'roundtrip',
        source:
          selectedOnward?.source === 'riya' || selectedReturn?.source === 'riya'
            ? 'riya'
            : selectedOnward?.source === 'offline' || selectedReturn?.source === 'offline'
              ? 'offline'
              : selectedOnward?.source || selectedReturn?.source || 'tripjack',
        onward: selectedOnward,
        return: selectedReturn,
        totalPrice: totalPrice,
        route: {
          from: fromCode,
          to: toCode,
          departureDate: departureDate,
          returnDate: returnDate
        },
        passengers: {
          adults,
          children,
          infants
        },
        travelClass
      };
      
      localStorage.setItem('selectedFlight', JSON.stringify(bookingData));
      localStorage.setItem('searchTimestamp', Date.now().toString());
      navigate(`/booking?onwardPriceId=${selectedOnward.priceId}&returnPriceId=${selectedReturn.priceId}&type=roundtrip&adults=${adults}&children=${children}&infants=${infants}`);
    }
  };

  // Handle Modify Search
  const handleModifySearch = (values) => {
    // If values are passed, use them (from DesktopModifySearch)
    // Otherwise use current state (from mobile modify)
    if (values) {
      const totalPax = values.adults + values.children + values.infants;
      navigate(`/round-trip?from=${values.fromAirport}&to=${values.toAirport}&date=${values.departureDate}&return=${values.returnDate}&pax=${totalPax}&adults=${values.adults}&children=${values.children}&infants=${values.infants}&class=${values.travelClass}&type=${values.tripType}`);
    } else {
      const totalPax = adults + children + infants;
      navigate(`/round-trip?from=${fromAirport}&to=${toAirport}&date=${departureDate}&return=${returnDate}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}&type=${tripType}`);
    }
    setShowModifySearch(false);
    setShowMobileModifySearch(false);
  };

  const handleSwapAirports = () => {
    const tmp = fromAirport;
    setFromAirport(toAirport);
    setToAirport(tmp);
  };

  /* ═══════════════════════════════════════════════════
     Airport Picker (Compact for Modify Search)
     ═══════════════════════════════════════════════════ */
  const AirportPicker = ({ label, selectedCode, onSelect }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);
    const ref = useRef(null);
    const info = airports.find((a) => a.code === selectedCode);

    const filtered = airports.filter(
      (a) =>
        a.city.toLowerCase().includes(search.toLowerCase()) ||
        a.code.toLowerCase().includes(search.toLowerCase()) ||
        a.name.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
      if (open) setTimeout(() => inputRef.current?.focus(), 50);
    }, [open]);

    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
      <div ref={ref} className="relative min-w-0">
        <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
        <button onClick={() => { setOpen(!open); setSearch(''); }} className="w-full text-left">
          <p className="text-lg font-extrabold text-[#1B375B] leading-tight">{selectedCode}</p>
          <p className="text-[10px] text-gray-400 truncate">{info?.city}</p>
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 overflow-hidden w-[310px] animate-fadeInUp">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="City, airport or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#659A9B] focus:bg-white transition-all"
                />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {filtered.map((a) => (
                <button
                  key={a.code}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-all hover:bg-[#659A9B]/5 ${a.code === selectedCode ? 'bg-[#659A9B]/10' : ''}`}
                  onClick={() => { onSelect(a.code); setOpen(false); setSearch(''); }}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#1B375B]">{a.code}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-[#1B375B] truncate">{a.city}, {a.country}</p>
                    <p className="text-[10px] text-gray-400 truncate">{a.name}</p>
                  </div>
                  {a.code === selectedCode && (
                    <div className="w-5 h-5 rounded-full bg-[#659A9B] flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                </button>
              ))}
              {filtered.length === 0 && <p className="text-center py-4 text-sm text-gray-400">No airports found</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════
     Date Picker (Compact for Modify Search)
     ═══════════════════════════════════════════════════ */
  const DatePicker = ({ label, date, onSelect, placeholder, minDate }) => {
    const [open, setOpen] = useState(false);
    const selected = date ? new Date(date) : undefined;

    const handleSelect = (day) => {
      if (day) {
        const yyyy = day.getFullYear();
        const mm = String(day.getMonth() + 1).padStart(2, '0');
        const dd = String(day.getDate()).padStart(2, '0');
        onSelect(`${yyyy}-${mm}-${dd}`);
        setOpen(false);
      }
    };

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="w-full text-left">
            <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
            {selected ? (
              <>
                <p className="text-base font-bold text-[#1B375B]">
                  {String(selected.getDate()).padStart(2, '0')} {format(selected, "MMM''yy")}
                </p>
                <p className="text-[10px] text-gray-400">{days[selected.getDay()]}</p>
              </>
            ) : (
              <p className="text-sm font-medium text-[#659A9B]">{placeholder || 'Select'}</p>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border-0" align="start" sideOffset={8}>
          <CalendarComponent
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            disabled={(d) => minDate ? d < new Date(minDate) : d < new Date(new Date().setHours(0,0,0,0))}
            initialFocus
            className="rounded-2xl"
            classNames={{
              day_selected: "bg-[#659A9B] text-white hover:bg-[#335E77] hover:text-white focus:bg-[#335E77] focus:text-white",
              day_today: "bg-[#659A9B]/10 text-[#1B375B] font-bold",
            }}
          />
        </PopoverContent>
      </Popover>
    );
  };

  /* ═══════════════════════════════════════════════════
     Passenger Selector (Compact for Modify Search)
     ═══════════════════════════════════════════════════ */
  const PassengerSelector = ({ adults: adultCount, children: childCount, infants: infantCount, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const total = adultCount + childCount + infantCount;

    useEffect(() => {
      const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    const Counter = ({ label, sublabel, icon, value, onInc, onDec, min = 0, max = 9 }) => (
      <div className="flex items-center justify-between py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#659A9B]/10 flex items-center justify-center">
            {icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1B375B]">{label}</p>
            <p className="text-[10px] text-gray-400">{sublabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={onDec}
            disabled={value <= min}
            className="w-8 h-8 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-[#659A9B] hover:text-[#659A9B] transition-colors text-gray-400 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="text-lg font-bold text-[#1B375B] w-6 text-center">{value}</span>
          <button
            onClick={onInc}
            disabled={value >= max}
            className="w-8 h-8 rounded-full border-2 border-[#659A9B] text-[#659A9B] flex items-center justify-center hover:bg-[#659A9B] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );

    return (
      <div ref={ref} className="relative">
        <p className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Travellers</p>
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-left">
          <p className="text-base font-bold text-[#1B375B]">{String(total).padStart(2, '0')} {adultCount === 1 ? 'Adult' : 'Traveller'}</p>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute top-full right-0 mt-2 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] border border-gray-100 z-50 w-[300px] animate-fadeInUp">
            <div className="px-5 pt-4 pb-1">
              <p className="text-xs font-bold text-[#1B375B] uppercase tracking-wider mb-1">Passengers</p>
            </div>
            <div className="px-5 divide-y divide-gray-100">
              <Counter
                label="Adults"
                sublabel="12+ years"
                icon={<CircleUser className="w-4 h-4 text-[#659A9B]" />}
                value={adultCount}
                onInc={() => onChange({ adults: adultCount + 1, children: childCount, infants: infantCount })}
                onDec={() => onChange({ adults: adultCount - 1, children: childCount, infants: infantCount })}
                min={1}
                max={9}
              />
              <Counter
                label="Children"
                sublabel="2 – 11 years"
                icon={<UserRound className="w-4 h-4 text-[#659A9B]" />}
                value={childCount}
                onInc={() => onChange({ adults: adultCount, children: childCount + 1, infants: infantCount })}
                onDec={() => onChange({ adults: adultCount, children: childCount - 1, infants: infantCount })}
                min={0}
                max={6}
              />
              <Counter
                label="Infants"
                sublabel="Under 2 years"
                icon={<Baby className="w-4 h-4 text-[#659A9B]" />}
                value={infantCount}
                onInc={() => onChange({ adults: adultCount, children: childCount, infants: infantCount + 1 })}
                onDec={() => onChange({ adults: adultCount, children: childCount, infants: infantCount - 1 })}
                min={0}
                max={adultCount}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-between items-center">
              <p className="text-xs text-gray-400">Infants must be fewer than adults</p>
              <button onClick={() => setOpen(false)} className="px-4 py-1.5 bg-[#1B375B] text-white rounded-lg text-xs font-semibold hover:bg-[#659A9B] transition-colors">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════
     Flight Card Component (Reusable)
     ═══════════════════════════════════════════════════ */
  const FlightCard = ({ flight, isSelected, onSelect, cheapestPrice, showExpanded }) => {
    const isExpanded = expandedFlight === flight.id;
    const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;
    
    return (
      <div className={`bg-white rounded-2xl border shadow-sm transition-all duration-300 overflow-hidden group ${
        isSoldOut ? 'opacity-60' : 'hover:shadow-lg'
      } ${
        isSelected ? 'border-[#659A9B] ring-2 ring-[#659A9B]/40' : 
        flight.price === cheapestPrice ? 'border-[#659A9B]/40 ring-1 ring-[#659A9B]/20' : 'border-gray-200/80'
      }`}>
        {/* Sold Out tag */}
        {isSoldOut && (
          <div data-testid="flight-card-rt-sold-out-badge" className="bg-gray-700 text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
            SOLD OUT
          </div>
        )}
        {/* Cheapest tag */}
        {!isSoldOut && flight.price === cheapestPrice && !isSelected && (
          <div className="bg-[#659A9B] text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> CHEAPEST
          </div>
        )}
        {isSelected && (
          <div className="bg-[#1B375B] text-white text-[10px] font-bold px-4 py-1 flex items-center gap-1.5">
            ✓ SELECTED
          </div>
        )}

        <div className="p-4" onClick={() => { if (!isSoldOut) onSelect(flight); }}>
          {/* Top Row: Flight Info */}
          <div className={`flex items-center gap-2 mb-3 ${isSoldOut ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            {/* Airline - Compact */}
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center flex-shrink-0">
                {airlineLogos[flight.airlineCode]?.square ? (
                  <img
                    src={airlineLogos[flight.airlineCode].square}
                    alt={flight.airline}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Plane className="w-4 h-4 text-[#659A9B]" />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-xs text-[#1B375B] truncate">{flight.airline}</p>
                <p className="text-[9px] text-gray-400">{flight.flightNumber}</p>
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* Price - Prominent Top Right */}
            <div className="text-right">
              <p className="text-xl font-black text-[#1B375B] leading-none">{formatINR(flight.price)}</p>
              <p className="text-[8px] text-gray-400 mt-0.5">per adult</p>
            </div>
          </div>

          {/* Flight Times and Duration */}
          <div className="flex items-center gap-2">
            {/* Departure */}
            <div className="text-center">
              <p className="text-xl font-extrabold text-[#1B375B] leading-none">{flight.departureTime}</p>
              <p className="text-[9px] text-gray-400 mt-0.5">{flight.from}</p>
            </div>

            {/* Duration line */}
            <div className="flex-1 flex flex-col items-center px-2">
              <div className="flex items-center gap-0 w-full">
                <div className="w-1.5 h-1.5 rounded-full border-2 border-[#1B375B]" />
                <div className="h-[2px] flex-1 bg-gradient-to-r from-[#1B375B] via-[#659A9B] to-[#1B375B]" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="text-[9px] text-gray-500 font-medium">{flight.duration}</span>
                <span className="text-[8px] bg-[#659A9B]/10 text-[#335E77] font-semibold px-1.5 py-0.5 rounded-full">
                  {flight.stops}
                </span>
              </div>
            </div>

            {/* Arrival */}
            <div className="text-center">
              <p className="text-xl font-extrabold text-[#1B375B] leading-none">{flight.arrivalTime}</p>
              <p className="text-[9px] text-gray-400 mt-0.5">{flight.to}</p>
            </div>
          </div>

          {/* Bottom row: details toggle + tags - Using ACTUAL Data */}
          {showExpanded && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1 text-[11px] text-gray-400">
                  <Luggage className="w-3 h-3" /> {flight.cabinBaggage || '7kg'} + {flight.baggage || '15kg'}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-gray-400">
                  <Utensils className="w-3 h-3" /> {flight.mealIncluded ? 'Free Meal' : 'No Meal'}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedFlight(expandedFlight === flight.id ? null : flight.id);
                }}
                className="flex items-center gap-1 text-xs text-[#659A9B] hover:text-[#335E77] font-semibold transition-colors"
              >
                Flight Details
                {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>

        {/* Expanded Flight Details - Using ACTUAL API Data */}
        {isExpanded && (
          <div className="border-t border-gray-100 bg-[#E7EEEE]/50 px-5 py-4">
            <div className="grid grid-cols-4 gap-6">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Aircraft</p>
                <p className="text-sm font-semibold text-[#1B375B]">
                  {flight.segments?.[0]?.fD?.eT || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Class</p>
                <p className="text-sm font-semibold text-[#1B375B]">{flight.class || 'Economy'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Baggage</p>
                <p className="text-sm font-semibold text-[#1B375B]">
                  {flight.cabinBaggage || '7 Kg'} Cabin + {flight.baggage || '15 Kg'} Check-in
                </p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Meal</p>
                <p className="text-sm font-semibold text-[#1B375B]">
                  {flight.mealIncluded ? 'Complimentary' : 'Not Included'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen travel-bg pb-20 md:pb-0 overflow-x-hidden">
      {/* ══ Desktop Modify Search ══ */}
      <DesktopModifySearch
        showModifySearch={showModifySearch}
        setShowModifySearch={setShowModifySearch}
        tripType={tripType}
        setTripType={setTripType}
        fromAirport={fromAirport}
        setFromAirport={setFromAirport}
        toAirport={toAirport}
        setToAirport={setToAirport}
        departureDate={departureDate}
        setDepartureDate={setDepartureDate}
        returnDate={returnDate}
        setReturnDate={setReturnDate}
        adults={adults}
        setAdults={setAdults}
        children={children}
        setChildren={setChildren}
        infants={infants}
        setInfants={setInfants}
        travelClass={travelClass}
        setTravelClass={setTravelClass}
        onSearch={handleModifySearch}
        showTripTypeSelector={false}
      />

      {/* ══════════ MOBILE VERSION ══════════ */}
      <div className="md:hidden overflow-x-hidden">
        {/* Mobile Header with Back Button */}
        <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 p-3">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors"
              aria-label="Back to home"
            >
              <ChevronLeft className="w-5 h-5 text-[#1B375B]" />
            </button>
            
            <button
              onClick={() => setShowMobileModifySearch(true)}
              className="flex-1 bg-gray-100 rounded-full px-4 py-3 active:bg-gray-200 transition-colors"
              style={{ minHeight: '48px' }}
            >
              <div className="text-center">
                <p className="font-bold text-[#1B375B] text-base">
                  {fromCode} ⇄ {toCode}
                </p>
                <p className="text-xs text-gray-500">
                  {departureDate ? new Date(departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Date'} - {returnDate ? new Date(returnDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Return'} • {adults + children + infants} Traveller • {travelClass}
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Side-by-side columns (Optimized - No horizontal scroll) */}
        <div className="px-2 flex gap-2 pb-24 overflow-x-hidden">
          {/* ONWARD COLUMN */}
          <div className="flex-1 space-y-2 min-w-0">
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200/50 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-[#1B375B]">ONWARD</p>
                <p className="text-[9px] text-gray-500 truncate">{fromCode} → {toCode}</p>
              </div>
              <button 
                onClick={() => setShowOnwardFilters(true)}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 flex-shrink-0"
              >
                <Filter className="w-3.5 h-3.5 text-[#1B375B]" />
              </button>
            </div>

            <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
              {/* Loading State - Mobile Skeleton */}
              {loading && (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <FlightSkeleton key={i} />
                  ))}
                </div>
              )}
              
              {!loading && paginatedOnwardFlights.map((flight, idx) => {
                const isSelected = selectedOnward?.id === flight.id;
                const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;
                
                return (
                  <div
                    key={flight.id}
                    onClick={() => { if (!isSoldOut) setSelectedOnward(flight); }}
                    className={`bg-white rounded-lg p-2 transition-all border ${
                      isSoldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    } ${
                      isSelected
                        ? 'border-[#659A9B] ring-1 ring-[#659A9B]/20 shadow-md'
                        : 'border-gray-200 shadow-sm'
                    }`}
                  >
                    {isSoldOut && (
                      <span data-testid="flight-mobile-rt-onward-sold-out" className="inline-block mb-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-gray-700 text-white">
                        SOLD OUT
                      </span>
                    )}
                    {/* Airline Logo & Name + Price */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex-shrink-0">
                        {airlineLogos[flight.airlineCode]?.square ? (
                          <img src={airlineLogos[flight.airlineCode].square} alt={flight.airline} className="w-8 h-8 object-contain" />
                        ) : (
                          <div className="w-8 h-8 flex items-center justify-center">
                            <Plane className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold text-gray-900 truncate">{flight.airline}</p>
                        <p className="text-[9px] text-gray-500">{flight.flightNumber}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[11px] font-extrabold text-[#1B375B] leading-tight">
                          {formatINR(flight.price)}
                        </div>
                        <div className="text-[8px] text-gray-400">per adult</div>
                      </div>
                    </div>

                    {/* Fare chips (Tripjack branded fares) */}
                    {flight.source === 'tripjack' && fareIdentifierChips(flight).length > 1 && (
                      <div className="flex flex-wrap gap-1 mb-1" role="radiogroup" aria-label="Fare options">
                        {fareIdentifierChips(flight).map(({ iden, delta, isLead }) => {
                          const active = String(flight.fareIdentifier || '').toUpperCase() === iden;
                          const disabled = specialReturnMode && iden !== 'SPECIAL_RETURN';
                          const label = iden === 'SPECIAL_RETURN' ? 'SPECIAL' : iden === 'TJ_FLEX' ? 'ACF Flex' : iden;
                          return (
                            <button
                              key={iden}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              disabled={disabled}
                              onClick={(e) => { e.stopPropagation(); setFareForOnwardFlight(flight, iden); }}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border transition-colors ${
                                disabled ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                : active ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <span>{label}</span>
                              {isLead && (
                                <span className={`px-1 rounded text-[8px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                                  Cheapest
                                </span>
                              )}
                              {!isLead && delta > 0 && (
                                <span className={`text-[8px] ${active ? 'text-white/80' : 'text-gray-500'}`}>
                                  +₹{delta.toLocaleString('en-IN')}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Flight Times */}
                    <div className="grid grid-cols-3 gap-1 items-center mb-1">
                      {/* Departure */}
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900 leading-tight">{flight.departureTime}</p>
                        <p className="text-[9px] text-gray-500">{flight.from}</p>
                        <p className="text-[8px] text-gray-400 truncate">{flight.fromCity}</p>
                      </div>

                      {/* Duration */}
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500">{flight.duration}</p>
                        <div className="h-px bg-gray-300 my-0.5" />
                        <p className="text-[8px] text-gray-500">{flight.stops}</p>
                      </div>

                      {/* Arrival */}
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 leading-tight">{flight.arrivalTime}</p>
                        <p className="text-[9px] text-gray-500">{flight.to}</p>
                        <p className="text-[8px] text-gray-400 truncate">{flight.toCity}</p>
                      </div>
                    </div>

                    {/* Price, Info and Details */}
                    <div className="mt-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 text-[8px] text-gray-500">
                          <span className="flex items-center gap-0.5">
                            <Luggage className="w-3 h-3" /> {flight.cabinBaggage} + {flight.baggage}
                          </span>
                          {flight.mealIncluded && (
                            <span className="flex items-center gap-0.5 text-green-600 font-medium">
                              <Utensils className="w-3 h-3" /> Meal
                            </span>
                          )}
                          {(() => {
                            const refundInfo = getRefundabilityInfo(flight.refundableType);
                            return (
                              <span className={`px-1.5 py-0.5 ${refundInfo.bgColor} ${refundInfo.textColor} text-[8px] font-semibold rounded border ${refundInfo.borderColor}`}>
                                {refundInfo.label}
                              </span>
                            );
                          })()}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const flightKey = `flight-${flight.id}`;
                            setExpandedFlight(expandedFlight === flightKey ? null : flightKey);
                          }}
                          className="text-[9px] text-teal-600 font-semibold flex items-center gap-0.5"
                        >
                          Details {expandedFlight === `flight-${flight.id}` ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      </div>

                      {/* Expandable Section */}
                      {expandedFlight === `flight-${flight.id}` && (
                        <div className="mt-2 pt-2 border-t border-gray-100 bg-gray-50 rounded -mx-2 px-2 pb-2">
                          <div className="flex border-b border-gray-300 mb-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab(prev => ({ ...prev, [`flight-${flight.id}`]: 'journey' }));
                              }}
                              className={`flex-1 py-1.5 text-[9px] font-semibold relative ${
                                (activeTab[`flight-${flight.id}`] || 'journey') === 'journey' ? 'text-teal-700' : 'text-gray-400'
                              }`}
                            >
                              Journey
                              {(activeTab[`flight-${flight.id}`] || 'journey') === 'journey' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab(prev => ({ ...prev, [`flight-${flight.id}`]: 'fareRules' }));
                                fetchFareRules(flight.priceId, `flight-${flight.id}`);
                              }}
                              className={`flex-1 py-1.5 text-[9px] font-semibold relative ${
                                activeTab[`flight-${flight.id}`] === 'fareRules' ? 'text-teal-700' : 'text-gray-400'
                              }`}
                            >
                              Fare Rules
                              {activeTab[`flight-${flight.id}`] === 'fareRules' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                              )}
                            </button>
                          </div>

                          {(activeTab[`flight-${flight.id}`] || 'journey') === 'journey' && (
                            <div className="space-y-1.5">
                              {flight.segments.map((seg, sidx) => (
                                <div key={sidx}>
                                  <div className="bg-white rounded p-1.5">
                                    <div className="flex justify-between items-start mb-1">
                                      <span className="text-[9px] font-bold text-gray-900">
                                        {seg.fD?.aI?.name} {seg.fD?.fN}
                                      </span>
                                      {seg.fD?.eT && (
                                        <span className="text-[7px] bg-gray-200 px-1 py-0.5 rounded">{seg.fD.eT}</span>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-3 gap-1 text-[8px]">
                                      <div>
                                        <p className="font-bold">{seg.dt?.split('T')[1]?.substring(0, 5)}</p>
                                        <p className="text-gray-500">{seg.da?.code}</p>
                                      </div>
                                      <div className="text-center text-gray-500">
                                        {Math.floor((seg.duration || 0) / 60)}h {(seg.duration || 0) % 60}m
                                      </div>
                                      <div className="text-right">
                                        <p className="font-bold">{seg.at?.split('T')[1]?.substring(0, 5)}</p>
                                        <p className="text-gray-500">{seg.aa?.code}</p>
                                      </div>
                                    </div>
                                  </div>
                                  {seg.cT && flight.segments[sidx + 1] && (
                                    <p className="text-[7px] text-orange-600 text-center py-0.5">
                                      Layover: {Math.floor(seg.cT / 60)}h {seg.cT % 60}m
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {activeTab[`flight-${flight.id}`] === 'fareRules' && (
                            <div>
                              {loadingFareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)] ? (
                                <div className="flex justify-center py-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                                </div>
                              ) : fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)]?.error ? (
                                <p className="text-[8px] text-red-600 text-center py-2 leading-snug">
                                  {fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)].error}
                                </p>
                              ) : (
                                <FareRulesCompact
                                  normalized={fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)]?._normalized}
                                  compact="tiny"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button - Onward Flights (Mobile) */}
            {!loading && hasMoreOnward && (
              <div className="flex flex-col items-center mt-2 gap-1">
                <button
                  onClick={handleLoadMoreOnward}
                  className="px-4 py-2 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-lg font-bold hover:bg-[#659A9B] hover:text-white transition-all shadow-sm flex items-center gap-2 text-xs"
                  data-testid="load-more-onward-mobile-btn"
                >
                  <span>Load More</span>
                  <span className="bg-[#659A9B] text-white px-1.5 py-0.5 rounded-full text-[10px]">
                    +{onwardFlights.length - onwardDisplayCount}
                  </span>
                </button>
                <div ref={onwardScrollRefMobile} className="h-1 w-full" aria-hidden="true" />
              </div>
            )}
          </div>

          {/* RETURN COLUMN */}
          <div className="flex-1 space-y-2 min-w-0">
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200/50 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-[#1B375B]">RETURN</p>
                <p className="text-[9px] text-gray-500 truncate">{toCode} → {fromCode}</p>
              </div>
              <button 
                onClick={() => setShowReturnFilters(true)}
                className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 flex-shrink-0"
              >
                <Filter className="w-3.5 h-3.5 text-[#1B375B]" />
              </button>
            </div>

            <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto">
              {/* Loading State - Mobile Skeleton */}
              {loading && (
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <FlightSkeleton key={i} />
                  ))}
                </div>
              )}
              
              {!loading && paginatedReturnFlights.map((flight, idx) => {
                const isSelected = selectedReturn?.id === flight.id;
                const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;
                
                return (
                  <div
                    key={flight.id}
                    onClick={() => { if (!isSoldOut) setSelectedReturn(flight); }}
                    className={`bg-white rounded-lg p-2 transition-all border ${
                      isSoldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                    } ${
                      isSelected
                        ? 'border-[#659A9B] ring-1 ring-[#659A9B]/20 shadow-md'
                        : 'border-gray-200 shadow-sm'
                    }`}
                  >
                    {isSoldOut && (
                      <span data-testid="flight-mobile-rt-return-sold-out" className="inline-block mb-1 px-1.5 py-0.5 text-[9px] font-bold rounded bg-gray-700 text-white">
                        SOLD OUT
                      </span>
                    )}
                    {/* Airline Logo & Name + Price */}
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex-shrink-0">
                        {airlineLogos[flight.airlineCode]?.square ? (
                          <img src={airlineLogos[flight.airlineCode].square} alt={flight.airline} className="w-8 h-8 object-contain" />
                        ) : (
                          <div className="w-8 h-8 flex items-center justify-center">
                            <Plane className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-semibold text-gray-900 truncate">{flight.airline}</p>
                        <p className="text-[9px] text-gray-500">{flight.flightNumber}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[11px] font-extrabold text-[#1B375B] leading-tight">
                          {formatINR(flight.price)}
                        </div>
                        <div className="text-[8px] text-gray-400">per adult</div>
                      </div>
                    </div>

                    {/* Fare chips (Tripjack branded fares) */}
                    {flight.source === 'tripjack' && fareIdentifierChips(flight).length > 1 && (
                      <div className="flex flex-wrap gap-1 mb-1" role="radiogroup" aria-label="Fare options">
                        {fareIdentifierChips(flight).map(({ iden, delta, isLead }) => {
                          const active = String(flight.fareIdentifier || '').toUpperCase() === iden;
                          const disabled = specialReturnMode && iden !== 'SPECIAL_RETURN';
                          const label = iden === 'SPECIAL_RETURN' ? 'SPECIAL' : iden === 'TJ_FLEX' ? 'ACF Flex' : iden;
                          return (
                            <button
                              key={iden}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              disabled={disabled}
                              onClick={(e) => { e.stopPropagation(); setFareForReturnFlight(flight, iden); }}
                              className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold border transition-colors ${
                                disabled ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                : active ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <span>{label}</span>
                              {isLead && (
                                <span className={`px-1 rounded text-[8px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                                  Cheapest
                                </span>
                              )}
                              {!isLead && delta > 0 && (
                                <span className={`text-[8px] ${active ? 'text-white/80' : 'text-gray-500'}`}>
                                  +₹{delta.toLocaleString('en-IN')}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Flight Times */}
                    <div className="grid grid-cols-3 gap-1 items-center mb-1">
                      {/* Departure */}
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900 leading-tight">{flight.departureTime}</p>
                        <p className="text-[9px] text-gray-500">{flight.from}</p>
                        <p className="text-[8px] text-gray-400 truncate">{flight.fromCity}</p>
                      </div>

                      {/* Duration */}
                      <div className="text-center">
                        <p className="text-[9px] text-gray-500">{flight.duration}</p>
                        <div className="h-px bg-gray-300 my-0.5" />
                        <p className="text-[8px] text-gray-500">{flight.stops}</p>
                      </div>

                      {/* Arrival */}
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900 leading-tight">{flight.arrivalTime}</p>
                        <p className="text-[9px] text-gray-500">{flight.to}</p>
                        <p className="text-[8px] text-gray-400 truncate">{flight.toCity}</p>
                      </div>
                    </div>

                    {/* Price, Info and Details */}
                    <div className="mt-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5 text-[8px] text-gray-500">
                          <span className="flex items-center gap-0.5">
                            <Luggage className="w-3 h-3" /> {flight.cabinBaggage} + {flight.baggage}
                          </span>
                          {flight.mealIncluded && (
                            <span className="flex items-center gap-0.5 text-green-600 font-medium">
                              <Utensils className="w-3 h-3" /> Meal
                            </span>
                          )}
                          {(() => {
                            const refundInfo = getRefundabilityInfo(flight.refundableType);
                            return (
                              <span className={`px-1.5 py-0.5 ${refundInfo.bgColor} ${refundInfo.textColor} text-[8px] font-semibold rounded border ${refundInfo.borderColor}`}>
                                {refundInfo.label}
                              </span>
                            );
                          })()}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const flightKey = `flight-${flight.id}`;
                            setExpandedFlight(expandedFlight === flightKey ? null : flightKey);
                          }}
                          className="text-[9px] text-teal-600 font-semibold flex items-center gap-0.5"
                        >
                          Details {expandedFlight === `flight-${flight.id}` ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </button>
                      </div>

                      {/* Expandable Section */}
                      {expandedFlight === `flight-${flight.id}` && (
                        <div className="mt-2 pt-2 border-t border-gray-100 bg-gray-50 rounded -mx-2 px-2 pb-2">
                          <div className="flex border-b border-gray-300 mb-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab(prev => ({ ...prev, [`flight-${flight.id}`]: 'journey' }));
                              }}
                              className={`flex-1 py-1.5 text-[9px] font-semibold relative ${
                                (activeTab[`flight-${flight.id}`] || 'journey') === 'journey' ? 'text-teal-700' : 'text-gray-400'
                              }`}
                            >
                              Journey
                              {(activeTab[`flight-${flight.id}`] || 'journey') === 'journey' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab(prev => ({ ...prev, [`flight-${flight.id}`]: 'fareRules' }));
                                fetchFareRules(flight.priceId, `flight-${flight.id}`);
                              }}
                              className={`flex-1 py-1.5 text-[9px] font-semibold relative ${
                                activeTab[`flight-${flight.id}`] === 'fareRules' ? 'text-teal-700' : 'text-gray-400'
                              }`}
                            >
                              Fare Rules
                              {activeTab[`flight-${flight.id}`] === 'fareRules' && (
                                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                              )}
                            </button>
                          </div>

                          {(activeTab[`flight-${flight.id}`] || 'journey') === 'journey' && (
                            <div className="space-y-1.5">
                              {flight.segments.map((seg, sidx) => (
                                <div key={sidx}>
                                  <div className="bg-white rounded p-1.5">
                                    <div className="flex justify-between items-start mb-1">
                                      <span className="text-[9px] font-bold text-gray-900">
                                        {seg.fD?.aI?.name} {seg.fD?.fN}
                                      </span>
                                      {seg.fD?.eT && (
                                        <span className="text-[7px] bg-gray-200 px-1 py-0.5 rounded">{seg.fD.eT}</span>
                                      )}
                                    </div>
                                    <div className="grid grid-cols-3 gap-1 text-[8px]">
                                      <div>
                                        <p className="font-bold">{seg.dt?.split('T')[1]?.substring(0, 5)}</p>
                                        <p className="text-gray-500">{seg.da?.code}</p>
                                      </div>
                                      <div className="text-center text-gray-500">
                                        {Math.floor((seg.duration || 0) / 60)}h {(seg.duration || 0) % 60}m
                                      </div>
                                      <div className="text-right">
                                        <p className="font-bold">{seg.at?.split('T')[1]?.substring(0, 5)}</p>
                                        <p className="text-gray-500">{seg.aa?.code}</p>
                                      </div>
                                    </div>
                                  </div>
                                  {seg.cT && flight.segments[sidx + 1] && (
                                    <p className="text-[7px] text-orange-600 text-center py-0.5">
                                      Layover: {Math.floor(seg.cT / 60)}h {seg.cT % 60}m
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {activeTab[`flight-${flight.id}`] === 'fareRules' && (
                            <div>
                              {loadingFareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)] ? (
                                <div className="flex justify-center py-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                                </div>
                              ) : fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)]?.error ? (
                                <p className="text-[8px] text-red-600 text-center py-2 leading-snug">
                                  {fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)].error}
                                </p>
                              ) : (
                                <FareRulesCompact
                                  normalized={fareRules[fareRuleKey(`flight-${flight.id}`, flight.priceId)]?._normalized}
                                  compact="tiny"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button - Return Flights (Mobile) */}
            {!loading && hasMoreReturn && (
              <div className="flex flex-col items-center mt-2 gap-1">
                <button
                  onClick={handleLoadMoreReturn}
                  className="px-4 py-2 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-lg font-bold hover:bg-[#659A9B] hover:text-white transition-all shadow-sm flex items-center gap-2 text-xs"
                  data-testid="load-more-return-mobile-btn"
                >
                  <span>Load More</span>
                  <span className="bg-[#659A9B] text-white px-1.5 py-0.5 rounded-full text-[10px]">
                    +{returnFlights.length - returnDisplayCount}
                  </span>
                </button>
                <div ref={returnScrollRefMobile} className="h-1 w-full" aria-hidden="true" />
              </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Sheets */}
        <div className="md:hidden">
          <BottomSheet isOpen={showMobileModifySearch} onClose={() => setShowMobileModifySearch(false)} title="Modify Search" height="tall">
          <div className="p-6 space-y-4">
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <AirportPicker label="From" selectedCode={fromAirport} onSelect={setFromAirport} />
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <AirportPicker label="To" selectedCode={toAirport} onSelect={setToAirport} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <DatePicker label="Departure" date={departureDate} onSelect={setDepartureDate} />
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <DatePicker label="Return" date={returnDate} onSelect={setReturnDate} minDate={departureDate} />
                </div>
              </div>
              <button
                onClick={handleModifySearch}
                className="w-full py-3.5 bg-[#1B375B] text-white rounded-xl font-bold active:scale-95 transition-transform"
              >
                Update Search
              </button>
            </div>
          </div>
        </BottomSheet>

        {/* Onward Filters Bottom Sheet */}
        <BottomSheet isOpen={showOnwardFilters} onClose={() => setShowOnwardFilters(false)} title="Onward Filters & Sort" height="tall">
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Sort By Section */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Sort By</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'price', label: 'Price' },
                  { id: 'duration', label: 'Fastest' },
                  { id: 'departure', label: 'Departure' },
                  { id: 'smart', label: 'Smart' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setOnwardSortBy(s.id)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      onwardSortBy === s.id
                        ? 'bg-[#1B375B] text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stops Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Stops</h3>
              <div className="space-y-2">
                {[
                  { value: 'direct', label: 'Direct', count: onwardFlights.filter(f => f.stops === 'Non-Stop').length },
                  { value: '1stop', label: '1 Stop', count: onwardFlights.filter(f => f.stops === '1 Stop').length },
                  { value: '2+', label: '2+ Stops', count: onwardFlights.filter(f => f.stops && f.stops.includes('2')).length },
                ].map((stop) => (
                  <label key={stop.value} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedStops.includes(stop.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStops([...selectedStops, stop.value]);
                          } else {
                            setSelectedStops(selectedStops.filter(s => s !== stop.value));
                          }
                        }}
                        className="w-4 h-4 text-[#1B375B] rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{stop.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">{stop.count}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Airlines Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Airlines</h3>
              <div className="space-y-2">
                {uniqueAirlines.slice(0, 5).map((airline) => (
                  <label key={airline.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedAirlines.includes(airline.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAirlines([...selectedAirlines, airline.code]);
                          } else {
                            setSelectedAirlines(selectedAirlines.filter(a => a !== airline.code));
                          }
                        }}
                        className="w-4 h-4 text-[#1B375B] rounded"
                      />
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        {airlineLogos[airline.code]?.square ? (
                          <img src={airlineLogos[airline.code].square} alt={airline.code} className="w-full h-full object-contain" />
                        ) : (
                          <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{airline.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{airline.count}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Departure Time Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Onward Departure Time</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'early', label: 'Early Morning', time: '00:00 - 06:00' },
                  { value: 'morning', label: 'Morning', time: '06:00 - 12:00' },
                  { value: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00' },
                  { value: 'evening', label: 'Evening', time: '18:00 - 24:00' },
                ].map((time) => (
                  <label key={time.value} className="cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedOnwardDepartureTimes.includes(time.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOnwardDepartureTimes([...selectedOnwardDepartureTimes, time.value]);
                        } else {
                          setSelectedOnwardDepartureTimes(selectedOnwardDepartureTimes.filter(t => t !== time.value));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="p-2.5 rounded-lg border-2 border-gray-200 peer-checked:border-[#1B375B] peer-checked:bg-blue-50 transition-all">
                      <p className="text-xs font-semibold text-gray-700">{time.label}</p>
                      <p className="text-[10px] text-gray-500">{time.time}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Arrival Time Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Onward Arrival Time</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'early', label: 'Early Morning', time: '00:00 - 06:00' },
                  { value: 'morning', label: 'Morning', time: '06:00 - 12:00' },
                  { value: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00' },
                  { value: 'evening', label: 'Evening', time: '18:00 - 24:00' },
                ].map((time) => (
                  <label key={time.value} className="cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedOnwardArrivalTimes.includes(time.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedOnwardArrivalTimes([...selectedOnwardArrivalTimes, time.value]);
                        } else {
                          setSelectedOnwardArrivalTimes(selectedOnwardArrivalTimes.filter(t => t !== time.value));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="p-2.5 rounded-lg border-2 border-gray-200 peer-checked:border-[#1B375B] peer-checked:bg-blue-50 transition-all">
                      <p className="text-xs font-semibold text-gray-700">{time.label}</p>
                      <p className="text-[10px] text-gray-500">{time.time}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Apply Button */}
            <button
              onClick={() => setShowOnwardFilters(false)}
              className="w-full py-3.5 bg-[#1B375B] text-white rounded-xl font-bold active:scale-95 transition-transform sticky bottom-0"
            >
              Apply Filters
            </button>
          </div>
        </BottomSheet>

        {/* Return Filters Bottom Sheet */}
        <BottomSheet isOpen={showReturnFilters} onClose={() => setShowReturnFilters(false)} title="Return Filters & Sort" height="tall">
          <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Sort By Section */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Sort By</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'price', label: 'Price' },
                  { id: 'duration', label: 'Fastest' },
                  { id: 'departure', label: 'Departure' },
                  { id: 'smart', label: 'Smart' },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setReturnSortBy(s.id)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      returnSortBy === s.id
                        ? 'bg-[#1B375B] text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stops Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Stops</h3>
              <div className="space-y-2">
                {[
                  { value: 'direct', label: 'Direct', count: returnFlights.filter(f => f.stops === 'Non-Stop').length },
                  { value: '1stop', label: '1 Stop', count: returnFlights.filter(f => f.stops === '1 Stop').length },
                  { value: '2+', label: '2+ Stops', count: returnFlights.filter(f => f.stops && f.stops.includes('2')).length },
                ].map((stop) => (
                  <label key={stop.value} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedStops.includes(stop.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStops([...selectedStops, stop.value]);
                          } else {
                            setSelectedStops(selectedStops.filter(s => s !== stop.value));
                          }
                        }}
                        className="w-4 h-4 text-[#1B375B] rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{stop.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">{stop.count}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Airlines Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Airlines</h3>
              <div className="space-y-2">
                {uniqueAirlines.slice(0, 5).map((airline) => (
                  <label key={airline.code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedAirlines.includes(airline.code)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAirlines([...selectedAirlines, airline.code]);
                          } else {
                            setSelectedAirlines(selectedAirlines.filter(a => a !== airline.code));
                          }
                        }}
                        className="w-4 h-4 text-[#1B375B] rounded"
                      />
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        {airlineLogos[airline.code]?.square ? (
                          <img src={airlineLogos[airline.code].square} alt={airline.code} className="w-full h-full object-contain" />
                        ) : (
                          <Plane className="w-3.5 h-3.5 text-[#659A9B]" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-700">{airline.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{airline.count}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Departure Time Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Return Departure Time</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'early', label: 'Early Morning', time: '00:00 - 06:00' },
                  { value: 'morning', label: 'Morning', time: '06:00 - 12:00' },
                  { value: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00' },
                  { value: 'evening', label: 'Evening', time: '18:00 - 24:00' },
                ].map((time) => (
                  <label key={time.value} className="cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReturnDepartureTimes.includes(time.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReturnDepartureTimes([...selectedReturnDepartureTimes, time.value]);
                        } else {
                          setSelectedReturnDepartureTimes(selectedReturnDepartureTimes.filter(t => t !== time.value));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="p-2.5 rounded-lg border-2 border-gray-200 peer-checked:border-[#1B375B] peer-checked:bg-blue-50 transition-all">
                      <p className="text-xs font-semibold text-gray-700">{time.label}</p>
                      <p className="text-[10px] text-gray-500">{time.time}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Arrival Time Filter */}
            <div>
              <h3 className="text-sm font-bold text-[#1B375B] mb-3">Return Arrival Time</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'early', label: 'Early Morning', time: '00:00 - 06:00' },
                  { value: 'morning', label: 'Morning', time: '06:00 - 12:00' },
                  { value: 'afternoon', label: 'Afternoon', time: '12:00 - 18:00' },
                  { value: 'evening', label: 'Evening', time: '18:00 - 24:00' },
                ].map((time) => (
                  <label key={time.value} className="cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedReturnArrivalTimes.includes(time.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedReturnArrivalTimes([...selectedReturnArrivalTimes, time.value]);
                        } else {
                          setSelectedReturnArrivalTimes(selectedReturnArrivalTimes.filter(t => t !== time.value));
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="p-2.5 rounded-lg border-2 border-gray-200 peer-checked:border-[#1B375B] peer-checked:bg-blue-50 transition-all">
                      <p className="text-xs font-semibold text-gray-700">{time.label}</p>
                      <p className="text-[10px] text-gray-500">{time.time}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Apply Button */}
            <button
              onClick={() => setShowReturnFilters(false)}
              className="w-full py-3.5 bg-[#1B375B] text-white rounded-xl font-bold active:scale-95 transition-transform sticky bottom-0"
            >
              Apply Filters
            </button>
          </div>
        </BottomSheet>
        </div>


        {/* Fixed Continue Button */}
        {bothSelected && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] z-50">
            <button
              onClick={handleContinue}
              className="w-full py-4 bg-[#1B375B] text-white rounded-xl font-bold text-lg shadow-lg active:scale-95 transition-transform"
            >
              Continue • ₹{totalPrice.toLocaleString()}
            </button>
          </div>
        )}
      </div>

      {/* ══════════ DESKTOP VERSION ══════════ */}
      <div className="hidden md:block">
        {/* ── Main Layout ── */}
        <div className="max-w-7xl mx-auto px-4 py-5 relative z-10">
          <div className="flex gap-5">

            {/* ══ LEFT: Filters ══ */}
            {/* ══ LEFT: Filters ══ */}
            <div className="hidden lg:block w-[260px] flex-shrink-0">
              <RoundTripFilterSidebar
                selectedStops={selectedStops}
                setSelectedStops={setSelectedStops}
                selectedAirlines={selectedAirlines}
                setSelectedAirlines={setSelectedAirlines}
                selectedOnwardDepartureTimes={selectedOnwardDepartureTimes}
                setSelectedOnwardDepartureTimes={setSelectedOnwardDepartureTimes}
                selectedOnwardArrivalTimes={selectedOnwardArrivalTimes}
                setSelectedOnwardArrivalTimes={setSelectedOnwardArrivalTimes}
                selectedReturnDepartureTimes={selectedReturnDepartureTimes}
                setSelectedReturnDepartureTimes={setSelectedReturnDepartureTimes}
                selectedReturnArrivalTimes={selectedReturnArrivalTimes}
                setSelectedReturnArrivalTimes={setSelectedReturnArrivalTimes}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                stopCounts={stopCounts}
                uniqueAirlines={uniqueAirlines}
                airlineLogos={airlineLogos}
              />
            </div>


            {/* ══ RIGHT: Side-by-Side Flight Columns ══ */}
            <div className="flex-1 min-w-0">
              {/* Two-Column Layout */}
              <div className="grid grid-cols-2 gap-5">
                {/* ONWARD FLIGHTS COLUMN */}
                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200/50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-base font-bold text-[#1B375B]">ONWARD</p>
                        <p className="text-sm text-gray-500">{fromCode} → {toCode} • {onwardFlights.length} Flights Available</p>
                      </div>
                    </div>

                    {/* Date Selector Strip for Onward - Coming Soon */}
                    <div className="mb-3 pb-3 border-b border-gray-100">
                      {/* Dynamic date pricing will be added later */}
                    </div>

                    {/* Sort Buttons for Onward */}
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl p-1">
                      {[
                        { id: 'price', label: 'Price' },
                        { id: 'duration', label: 'Fastest' },
                        { id: 'departure', label: 'Departure' },
                        { id: 'smart', label: 'Smart' },
                      ].map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setOnwardSortBy(s.id)}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                            onwardSortBy === s.id
                              ? 'bg-white text-[#1B375B] shadow-sm'
                              : 'text-gray-500 hover:text-[#1B375B]'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Loading State - Desktop Onward Skeleton */}
                  {loading && (
                    <div className="space-y-3">
                      {[...Array(6)].map((_, i) => (
                        <FlightSkeleton key={i} />
                      ))}
                    </div>
                  )}

                  {/* Onward Flight Cards - Desktop with Full Details */}
                  {!loading && paginatedOnwardFlights.map(flight => {
                    const isSelected = selectedOnward?.id === flight.id;
                    const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;
                    const flightKey = `desktop-onward-${flight.id}`;
                    
                    return (
                      <div
                        key={flight.id}
                        onClick={() => { if (!isSoldOut) setSelectedOnward(flight); }}
                        className={`bg-white rounded-xl p-4 transition-all border ${
                          isSoldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                        } ${
                          isSelected
                            ? 'border-[#659A9B] ring-2 ring-[#659A9B]/20 shadow-lg'
                            : 'border-gray-200 shadow-sm'
                        }`}
                      >
                        {isSoldOut && (
                          <div data-testid="flight-desktop-rt-onward-sold-out" className="mb-2 inline-block bg-gray-700 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                            SOLD OUT
                          </div>
                        )}
                        {/* Flight Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {airlineLogos[flight.airlineCode]?.square ? (
                              <img src={airlineLogos[flight.airlineCode].square} alt={flight.airline} className="w-12 h-12 object-contain" />
                            ) : (
                              <div className="w-12 h-12 flex items-center justify-center">
                                <Plane className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-bold text-gray-900">{flight.airline}</p>
                              <p className="text-xs text-gray-500">{flight.flightNumber}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-extrabold text-[#1B375B] leading-none">{formatINR(flight.price)}</div>
                            <div className="text-[10px] text-gray-400">per adult</div>
                          </div>
                          {isSelected && (
                            <div className="bg-teal-600 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              ✓ SELECTED
                            </div>
                          )}
                        </div>

                        {/* Fare chips (Tripjack branded fares) */}
                        {flight.source === 'tripjack' && fareIdentifierChips(flight).length > 1 && (
                          <div className="flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="Fare options">
                            {fareIdentifierChips(flight).map(({ iden, delta, isLead }) => {
                              const active = String(flight.fareIdentifier || '').toUpperCase() === iden;
                              const disabled = specialReturnMode && iden !== 'SPECIAL_RETURN';
                              const label = iden === 'SPECIAL_RETURN' ? 'SPECIAL RETURN' : iden === 'TJ_FLEX' ? 'ACF Flex' : iden;
                              return (
                                <button
                                  key={iden}
                                  type="button"
                                  role="radio"
                                  aria-checked={active}
                                  disabled={disabled}
                                  onClick={(e) => { e.stopPropagation(); setFareForOnwardFlight(flight, iden); }}
                                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                                    disabled ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                    : active ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <span>{label}</span>
                                  {isLead && (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                                      Cheapest
                                    </span>
                                  )}
                                  {!isLead && delta > 0 && (
                                    <span className={`text-[10px] ${active ? 'text-white/80' : 'text-gray-500'}`}>
                                      +₹{delta.toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Flight Route Info */}
                        <div className="grid grid-cols-5 gap-3 items-center mb-3">
                          <div className="col-span-2 text-left">
                            <p className="text-2xl font-bold text-gray-900">{flight.departureTime}</p>
                            <p className="text-xs font-semibold text-gray-600">{flight.from}</p>
                            <p className="text-xs text-gray-500">{flight.fromCity}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">{flight.duration}</p>
                            <div className="h-px bg-gray-300" />
                            <p className="text-xs text-gray-600 mt-1">{flight.stops}</p>
                          </div>
                          <div className="col-span-2 text-right">
                            <p className="text-2xl font-bold text-gray-900">{flight.arrivalTime}</p>
                            <p className="text-xs font-semibold text-gray-600">{flight.to}</p>
                            <p className="text-xs text-gray-500">{flight.toCity}</p>
                          </div>
                        </div>

                        {/* Flight Info & Details Toggle */}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Luggage className="w-4 h-4" /> {flight.cabinBaggage} + {flight.baggage}
                            </span>
                            {flight.mealIncluded && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <Utensils className="w-4 h-4" /> Meal
                              </span>
                            )}
                            {(() => {
                              const refundInfo = getRefundabilityInfo(flight.refundableType);
                              return (
                                <span className={`px-2 py-0.5 ${refundInfo.bgColor} ${refundInfo.textColor} text-xs font-semibold rounded border ${refundInfo.borderColor}`}>
                                  {refundInfo.label}
                                </span>
                              );
                            })()}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedFlight(expandedFlight === flightKey ? null : flightKey);
                            }}
                            className="text-sm text-teal-600 font-semibold flex items-center gap-1 hover:text-teal-700"
                          >
                            Flight Details {expandedFlight === flightKey ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>

                        {/* Expandable Details Section */}
                        {expandedFlight === flightKey && (
                          <div className="mt-4 pt-4 border-t border-gray-200 bg-gray-50 rounded-lg p-4">
                            {/* Tabs */}
                            <div className="flex border-b border-gray-300 mb-4">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'journey' }));
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'journey' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Journey Details
                                {(activeTab[flightKey] || 'journey') === 'journey' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'fare' }));
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'fare' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Fare Details
                                {(activeTab[flightKey] || 'journey') === 'fare' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'fareRules' }));
                                  fetchFareRules(flight.priceId, flightKey);
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'fareRules' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Fare Rules
                                {(activeTab[flightKey] || 'journey') === 'fareRules' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                            </div>

                            {/* Tab Content */}
                            {(activeTab[flightKey] || 'journey') === 'journey' ? (
                              <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-16 text-center">
                                    <p className="text-sm font-bold text-gray-900">{flight.departureTime}</p>
                                    <p className="text-xs text-gray-500">{new Date(flight.departureDateTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-bold text-gray-900">{flight.fromCity} ({flight.from})</p>
                                    <p className="text-xs text-gray-500">{flight.fromAirport}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 pl-16">
                                  <div className="w-px h-8 bg-gray-300"></div>
                                  <p className="text-xs text-gray-500">{flight.duration}</p>
                                </div>
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-16 text-center">
                                    <p className="text-sm font-bold text-gray-900">{flight.arrivalTime}</p>
                                    <p className="text-xs text-gray-500">{new Date(flight.arrivalDateTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-bold text-gray-900">{flight.toCity} ({flight.to})</p>
                                    <p className="text-xs text-gray-500">{flight.toAirport}</p>
                                  </div>
                                </div>
                              </div>
                            ) : (activeTab[flightKey] || 'journey') === 'fare' ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Base Fare:</span>
                                  <span className="font-semibold">₹{flight.baseFare?.toLocaleString() || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Taxes & Fees:</span>
                                  <span className="font-semibold">₹{flight.taxesAndFees?.toLocaleString() || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-gray-300">
                                  <span className="text-gray-900 font-bold">Total:</span>
                                  <span className="text-lg font-bold text-[#1B375B]">{formatINR(flight.price)}</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {loadingFareRules[fareRuleKey(flightKey, flight.priceId)] ? (
                                  <div className="flex justify-center py-6">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                                  </div>
                                ) : fareRules[fareRuleKey(flightKey, flight.priceId)]?.error ? (
                                  <p className="text-sm text-red-600 text-center py-4">
                                    {fareRules[fareRuleKey(flightKey, flight.priceId)].error}
                                  </p>
                                ) : (
                                  <FareRulesCompact
                                    normalized={fareRules[fareRuleKey(flightKey, flight.priceId)]?._normalized}
                                    compact="normal"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Load More Button - Desktop Onward */}
                  {!loading && hasMoreOnward && (
                    <div className="flex flex-col items-center mt-3 gap-1">
                      <button
                        onClick={handleLoadMoreOnward}
                        className="px-6 py-2.5 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-xl font-bold hover:bg-[#659A9B] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
                        data-testid="load-more-onward-desktop-btn"
                      >
                        <span>Load More Flights</span>
                        <span className="bg-[#659A9B] text-white px-2 py-0.5 rounded-full text-xs">
                          +{onwardFlights.length - onwardDisplayCount}
                        </span>
                      </button>
                      <div ref={onwardScrollRefDesktop} className="h-1 w-full" aria-hidden="true" />
                    </div>
                  )}
                </div>

                {/* RETURN FLIGHTS COLUMN */}
                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200/50">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-base font-bold text-[#1B375B]">RETURN</p>
                        <p className="text-sm text-gray-500">{toCode} → {fromCode} • {returnFlights.length} Flights Available</p>
                      </div>
                    </div>

                    {/* Audit §6.4 (May-2026) — Special-Return UI pre-warn.
                        Surfaces sri/msri pairing constraint at fare-selection
                        time so users see WHY the return list shrinks (or is
                        empty) when they select an onward SPECIAL_RETURN fare.
                        Backend still enforces this at /book; this is UX. */}
                    {specialReturnMode && selectedOnward?.sri && returnFlights.length === 0 ? (
                      <div
                        className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
                        data-testid="special-return-no-pairs-warning"
                      >
                        <p className="text-xs text-amber-900 leading-snug">
                          <span className="font-semibold">No paired return fares.</span>{' '}
                          The selected onward Special Return fare has no matching return options. Select a different onward fare or switch off Special Return.
                        </p>
                      </div>
                    ) : null}
                    {specialReturnMode && !selectedOnward?.sri ? (
                      <div
                        className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
                        data-testid="special-return-pick-onward-hint"
                      >
                        <p className="text-xs text-blue-900 leading-snug">
                          <span className="font-semibold">Pick an onward fare first.</span>{' '}
                          Special Return pairs the return fare with your onward selection. Return options will update automatically.
                        </p>
                      </div>
                    ) : null}

                    {/* Date Selector Strip for Return - Coming Soon */}
                    <div className="mb-3 pb-3 border-b border-gray-100">
                      {/* Dynamic date pricing will be added later */}
                    </div>

                    {/* Sort Buttons for Return */}
                    <div className="flex items-center gap-1.5 bg-gray-50 rounded-xl p-1">
                      {[
                        { id: 'price', label: 'Price' },
                        { id: 'duration', label: 'Fastest' },
                        { id: 'departure', label: 'Departure' },
                        { id: 'smart', label: 'Smart' },
                      ].map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setReturnSortBy(s.id)}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                            returnSortBy === s.id
                              ? 'bg-white text-[#1B375B] shadow-sm'
                              : 'text-gray-500 hover:text-[#1B375B]'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Loading State - Desktop Return Skeleton */}
                  {loading && (
                    <div className="space-y-3">
                      {[...Array(6)].map((_, i) => (
                        <FlightSkeleton key={i} />
                      ))}
                    </div>
                  )}

                  {/* Return Flight Cards - Desktop with Full Details */}
                  {!loading && paginatedReturnFlights.map(flight => {
                    const isSelected = selectedReturn?.id === flight.id;
                    const isSoldOut = flight.isSoldOut || flight.seatsLeft === 0;
                    const flightKey = `desktop-return-${flight.id}`;
                    
                    return (
                      <div
                        key={flight.id}
                        onClick={() => { if (!isSoldOut) setSelectedReturn(flight); }}
                        className={`bg-white rounded-xl p-4 transition-all border ${
                          isSoldOut ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                        } ${
                          isSelected
                            ? 'border-[#659A9B] ring-2 ring-[#659A9B]/20 shadow-lg'
                            : 'border-gray-200 shadow-sm'
                        }`}
                      >
                        {isSoldOut && (
                          <div data-testid="flight-desktop-rt-return-sold-out" className="mb-2 inline-block bg-gray-700 text-white text-[10px] font-bold px-3 py-1 rounded-full">
                            SOLD OUT
                          </div>
                        )}
                        {/* Flight Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {airlineLogos[flight.airlineCode]?.square ? (
                              <img src={airlineLogos[flight.airlineCode].square} alt={flight.airline} className="w-12 h-12 object-contain" />
                            ) : (
                              <div className="w-12 h-12 flex items-center justify-center">
                                <Plane className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-bold text-gray-900">{flight.airline}</p>
                              <p className="text-xs text-gray-500">{flight.flightNumber}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-extrabold text-[#1B375B] leading-none">{formatINR(flight.price)}</div>
                            <div className="text-[10px] text-gray-400">per adult</div>
                          </div>
                          {isSelected && (
                            <div className="bg-teal-600 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              ✓ SELECTED
                            </div>
                          )}
                        </div>

                        {/* Fare chips (Tripjack branded fares) */}
                        {flight.source === 'tripjack' && fareIdentifierChips(flight).length > 1 && (
                          <div className="flex flex-wrap gap-2 mb-3" role="radiogroup" aria-label="Fare options">
                            {fareIdentifierChips(flight).map(({ iden, delta, isLead }) => {
                              const active = String(flight.fareIdentifier || '').toUpperCase() === iden;
                              const disabled = specialReturnMode && iden !== 'SPECIAL_RETURN';
                              const label = iden === 'SPECIAL_RETURN' ? 'SPECIAL RETURN' : iden === 'TJ_FLEX' ? 'ACF Flex' : iden;
                              return (
                                <button
                                  key={iden}
                                  type="button"
                                  role="radio"
                                  aria-checked={active}
                                  disabled={disabled}
                                  onClick={(e) => { e.stopPropagation(); setFareForReturnFlight(flight, iden); }}
                                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                                    disabled ? 'opacity-40 cursor-not-allowed border-gray-200 text-gray-400'
                                    : active ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <span>{label}</span>
                                  {isLead && (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                                      Cheapest
                                    </span>
                                  )}
                                  {!isLead && delta > 0 && (
                                    <span className={`text-[10px] ${active ? 'text-white/80' : 'text-gray-500'}`}>
                                      +₹{delta.toLocaleString('en-IN')}
                                    </span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Flight Route Info */}
                        <div className="grid grid-cols-5 gap-3 items-center mb-3">
                          <div className="col-span-2 text-left">
                            <p className="text-2xl font-bold text-gray-900">{flight.departureTime}</p>
                            <p className="text-xs font-semibold text-gray-600">{flight.from}</p>
                            <p className="text-xs text-gray-500">{flight.fromCity}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 mb-1">{flight.duration}</p>
                            <div className="h-px bg-gray-300" />
                            <p className="text-xs text-gray-600 mt-1">{flight.stops}</p>
                          </div>
                          <div className="col-span-2 text-right">
                            <p className="text-2xl font-bold text-gray-900">{flight.arrivalTime}</p>
                            <p className="text-xs font-semibold text-gray-600">{flight.to}</p>
                            <p className="text-xs text-gray-500">{flight.toCity}</p>
                          </div>
                        </div>

                        {/* Flight Info & Details Toggle */}
                        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Luggage className="w-4 h-4" /> {flight.cabinBaggage} + {flight.baggage}
                            </span>
                            {flight.mealIncluded && (
                              <span className="flex items-center gap-1 text-green-600 font-medium">
                                <Utensils className="w-4 h-4" /> Meal
                              </span>
                            )}
                            {(() => {
                              const refundInfo = getRefundabilityInfo(flight.refundableType);
                              return (
                                <span className={`px-2 py-0.5 ${refundInfo.bgColor} ${refundInfo.textColor} text-xs font-semibold rounded border ${refundInfo.borderColor}`}>
                                  {refundInfo.label}
                                </span>
                              );
                            })()}
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedFlight(expandedFlight === flightKey ? null : flightKey);
                            }}
                            className="text-sm text-teal-600 font-semibold flex items-center gap-1 hover:text-teal-700"
                          >
                            Flight Details {expandedFlight === flightKey ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>

                        {/* Expandable Details Section */}
                        {expandedFlight === flightKey && (
                          <div className="mt-4 pt-4 border-t border-gray-200 bg-gray-50 rounded-lg p-4">
                            {/* Tabs */}
                            <div className="flex border-b border-gray-300 mb-4">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'journey' }));
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'journey' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Journey Details
                                {(activeTab[flightKey] || 'journey') === 'journey' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'fare' }));
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'fare' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Fare Details
                                {(activeTab[flightKey] || 'journey') === 'fare' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTab(prev => ({ ...prev, [flightKey]: 'fareRules' }));
                                  fetchFareRules(flight.priceId, flightKey);
                                }}
                                className={`flex-1 py-2 text-sm font-semibold relative ${
                                  (activeTab[flightKey] || 'journey') === 'fareRules' ? 'text-teal-700' : 'text-gray-400'
                                }`}
                              >
                                Fare Rules
                                {(activeTab[flightKey] || 'journey') === 'fareRules' && (
                                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                                )}
                              </button>
                            </div>

                            {/* Tab Content */}
                            {(activeTab[flightKey] || 'journey') === 'journey' ? (
                              <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-16 text-center">
                                    <p className="text-sm font-bold text-gray-900">{flight.departureTime}</p>
                                    <p className="text-xs text-gray-500">{new Date(flight.departureDateTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-bold text-gray-900">{flight.fromCity} ({flight.from})</p>
                                    <p className="text-xs text-gray-500">{flight.fromAirport}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 pl-16">
                                  <div className="w-px h-8 bg-gray-300"></div>
                                  <p className="text-xs text-gray-500">{flight.duration}</p>
                                </div>
                                <div className="flex items-start gap-3">
                                  <div className="flex-shrink-0 w-16 text-center">
                                    <p className="text-sm font-bold text-gray-900">{flight.arrivalTime}</p>
                                    <p className="text-xs text-gray-500">{new Date(flight.arrivalDateTime).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</p>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-bold text-gray-900">{flight.toCity} ({flight.to})</p>
                                    <p className="text-xs text-gray-500">{flight.toAirport}</p>
                                  </div>
                                </div>
                              </div>
                            ) : (activeTab[flightKey] || 'journey') === 'fare' ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Base Fare:</span>
                                  <span className="font-semibold">₹{flight.baseFare?.toLocaleString() || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Taxes & Fees:</span>
                                  <span className="font-semibold">₹{flight.taxesAndFees?.toLocaleString() || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-gray-300">
                                  <span className="text-gray-900 font-bold">Total:</span>
                                  <span className="text-lg font-bold text-[#1B375B]">{formatINR(flight.price)}</span>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {loadingFareRules[fareRuleKey(flightKey, flight.priceId)] ? (
                                  <div className="flex justify-center py-6">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                                  </div>
                                ) : fareRules[fareRuleKey(flightKey, flight.priceId)]?.error ? (
                                  <p className="text-sm text-red-600 text-center py-4">
                                    {fareRules[fareRuleKey(flightKey, flight.priceId)].error}
                                  </p>
                                ) : (
                                  <FareRulesCompact
                                    normalized={fareRules[fareRuleKey(flightKey, flight.priceId)]?._normalized}
                                    compact="normal"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Load More Button - Desktop Return */}
                  {!loading && hasMoreReturn && (
                    <div className="flex flex-col items-center mt-3 gap-1">
                      <button
                        onClick={handleLoadMoreReturn}
                        className="px-6 py-2.5 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-xl font-bold hover:bg-[#659A9B] hover:text-white transition-all shadow-sm flex items-center gap-2 text-sm"
                        data-testid="load-more-return-desktop-btn"
                      >
                        <span>Load More Flights</span>
                        <span className="bg-[#659A9B] text-white px-2 py-0.5 rounded-full text-xs">
                          +{returnFlights.length - returnDisplayCount}
                        </span>
                      </button>
                      <div ref={returnScrollRefDesktop} className="h-1 w-full" aria-hidden="true" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Continue Button with Selected Flight Details (Desktop) */}
        {bothSelected && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)] z-50">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between gap-6">
                {/* Left: Selected Flights Summary */}
                <div className="flex items-center gap-8 flex-1">
                  {/* Onward Flight Summary */}
                  <div className="flex items-center gap-3">
                    <div className="bg-[#659A9B]/10 px-3 py-1.5 rounded-lg">
                      <p className="text-[9px] font-bold text-[#335E77] uppercase tracking-wider">Onward</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        {airlineLogos[selectedOnward?.airlineCode]?.square ? (
                          <img
                            src={airlineLogos[selectedOnward.airlineCode].square}
                            alt={selectedOnward.airline}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Plane className="w-4 h-4 text-[#659A9B]" />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#1B375B] leading-tight">{selectedOnward.airline}</p>
                        <p className="text-[9px] text-gray-500">
                          {selectedOnward.departureTime} - {selectedOnward.arrivalTime} • {selectedOnward.stops}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="w-px h-12 bg-gray-200"></div>

                  {/* Return Flight Summary */}
                  <div className="flex items-center gap-3">
                    <div className="bg-[#1B375B]/10 px-3 py-1.5 rounded-lg">
                      <p className="text-[9px] font-bold text-[#1B375B] uppercase tracking-wider">Return</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        {airlineLogos[selectedReturn?.airlineCode]?.square ? (
                          <img
                            src={airlineLogos[selectedReturn.airlineCode].square}
                            alt={selectedReturn.airline}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Plane className="w-4 h-4 text-[#659A9B]" />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-[#1B375B] leading-tight">{selectedReturn.airline}</p>
                        <p className="text-[9px] text-gray-500">
                          {selectedReturn.departureTime} - {selectedReturn.arrivalTime} • {selectedReturn.stops}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right: Price + Button */}
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Total Price ({adults} {adults > 1 ? 'Adults' : 'Adult'})</p>
                    <p className="text-3xl font-black text-[#1B375B] leading-tight">₹{totalPrice.toLocaleString()}</p>
                  </div>
                  <button
                    onClick={handleContinue}
                    className="px-10 py-4 bg-[#1B375B] text-white rounded-xl text-base font-bold hover:bg-[#659A9B] transition-all shadow-lg hover:shadow-xl"
                  >
                    Continue to Booking
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoundTripResults;
